import { faUserCircle, faTrash } from "@fortawesome/pro-light-svg-icons";
import AwesomeIcon from "../AwesomeIcon";
import classNames from "classnames";
import { useContext, useEffect, useMemo, useRef, useState } from "react";
import Tag from "./Tag";
import DropdownMenu from "../FormComponents/DropdownMenu";
import DateRangeInput from "../FormComponents/DateRangeInput";
import DropdownTag from "./DropdownTag";
import { get, isEmpty, isEqual, xor, remove, size, cloneDeep } from "lodash";
import { applyFilterOrder } from "../../utils/filterOrder";
import { PatientsContext } from "../Patients/PatientsContext";
import {
  formatFiltersObj,
  getFilterString,
  parseRemindersToTaskFilters,
  buildReminders,
  areFormattedFiltersEqual,
} from "./utils";
import Toast from "../Toast";
import SingleSelectTag from "../FormComponents/SingleSelectTag";
import useAddFilter from "./services/useAddFilter";
import useDeleteFilter from "./services/useDeleteFilter";
import useEditFilter from "./services/useEditFilter";
import DebounceInput from "../common/DebounceInput";
import { useStoreState } from "pullstate";
import Store from "../../utils/Store";
import constants from "../../utils/constants";
import { useTranslation } from "react-i18next";
import useHandleClickOutside from "../../hooks/useHandleClickOutside";
import useCheckBulkActions from "../../hooks/useCheckBulkActions";
import SelectUser from "./SelectUser";
import Loader from "../Loader";
import { useApplyFilterAction } from "../Assistant/utils/actions/useApplyFilterAction";
import {
  faBarsFilter,
  faFilterList,
  faMagnifyingGlass,
  faPenToSquare,
  faXmark,
} from "@fortawesome/pro-regular-svg-icons";
import FlyoutMenu from "../FlyoutMenu";
import RadioList from "../FormComponents/RadioList";
import FindingsSelector from "./FindingsSelector";
import { getIsOnpremise, isWebView } from "../../utils";
import { trackEvent } from "../../utils/mixpanel";
import { MIXPANEL_EVENTS } from "../../utils/mixpanelEvents";
import useMedia, { MediaSizes } from "../../hooks/useMedia";
import { differenceInDays, startOfDay, endOfDay } from "date-fns";
import CustomFieldsFilters from "./CustomFieldsFilters";
import useAddCustomFilters from "../Assistant/utils/actions/useAddCustomFilters";
import { useAuth } from "@qure/qure-auth";
import useWorkspaceTagSettingsService from "../CustomTagsManagement/services/workspaceTagSettingsService";
import Modal from "../common/Modal";
import Button from "../common/Button";
import { DEFAULT_SEARCH_FILTERS, EMPTY_STATE, INITIAL_FILTERS_CONFIG } from "./constants";
import isFiltersDataEmpty from "../../utils/isFiltersDataEmpty";
import { useAllPermittedUsers } from "../WorkspaceManagement/services/useAllPermittedUsers";
import { compLettersBeforeNumbers } from "../../utils/string-compare.util";
import useGetTaskLabels from "../PatientReminder/services/useGetTaskLabels";
import { requestWorklistFilterChange } from "../../utils/worklistFilterNavigation";

const mergeCustomTagsByTagId = (baseTags = [], incomingTags = []) => {
  const byTagId = new Map();
  const order = [];
  [...(baseTags || []), ...(incomingTags || [])].forEach((tag) => {
    if (!tag) return;
    const tagId = tag.tag_id ?? tag.id;
    const values = Array.isArray(tag.value)
      ? tag.value
      : tag.value != null
      ? [tag.value]
      : [];
    if (byTagId.has(tagId)) {
      const existing = byTagId.get(tagId);
      existing.value = [...new Set([...existing.value, ...values])];
    } else {
      byTagId.set(tagId, { ...tag, value: [...new Set(values)] });
      order.push(tagId);
    }
  });
  return order.map((tagId) => byTagId.get(tagId));
};

const SearchFilters = ({
  onChange = () => {},
  patients = [],
  filters,
  patientsCount,
  isCountLoading,
  setFilters,
  setLastId,
  setPatientsData,
  labelMappings,
  savedFilterData,
  setSavedFilterData,
  tagsList,
  isFullWidthWorklist,
  onChangeSearch = (string) => {},
  showFilters,
  setShowFilters,
  isLoading = false,
  isLC = false,
  selectedWorkspace = null, //Only for LC bulk actions page
  enableNewFilterLayout = false, // Feature flag for new filter layout with Task/Patient groups
}) => {
  const { t } = useTranslation();
  const ref = useRef();
  const isOnpremise = getIsOnpremise();
  const isMobile = useMedia([MediaSizes.MOBILE], [true], false);
  const isBulkActions = useCheckBulkActions();
  const [parentWidth, setParentWidth] = useState(null);

  //profile data
  const { userInfo, refetchProfile } = useAuth();
  const { filterOrder } = useContext(PatientsContext);
  const { appConfig, isWorklistCollapsed } = useStoreState(Store, (s) => ({
    appConfig: s.appConfig,
    isWorklistCollapsed: s.isWorklistCollapsed,
  }));

  // Fetch permitted users for task owner filter
  const { data: assignableUserList } = useAllPermittedUsers(
    constants.WORKSPACE_USER_ROLES.WORKSPACE_TASK_ASSIGNABLE
  );
  // Fetch workspace tags for LC-specific filter options
  const workspaceId = selectedWorkspace?.value || userInfo?.default_workspace;
  const { data: workspaceTags = [] } = useWorkspaceTagSettingsService(workspaceId);

  const [filtersData, setFiltersData] = useState(DEFAULT_SEARCH_FILTERS);
  //const [showFilters, setShowFilters] = useState(false);
  const [isSaveInput, setIsSaveInput] = useState(false);
  const [savedFilterName, setSavedFilterName] = useState("");
  const [selectedSingleTag, setSelectedSingleTag] = useState();
  const [showIcon, setShowIcon] = useState(true);
  const [isPromptDate, setIsPromptDate] = useState(false);
  const [hoveredFilterId, setHoveredFilterId] = useState(null);
  // State for tracking selected filters in modal (for multi-select when isLC && enableNewFilterLayout)
  const [selectedFiltersInModal, setSelectedFiltersInModal] = useState([]);
  // State for delete confirmation modal
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [filterToDelete, setFilterToDelete] = useState({ id: null, label: null });
  const { hasClickOutside } = useHandleClickOutside(ref);
  const newFilterAdded = useRef("");
  const hasInitializedModalSelection = useRef(false);
  const hasInitializedFiltersData = useRef(false);
  const previousSelectedFiltersInModal = useRef([]);

  const present_findings_selected_dropdown_tags = Object.values(
    get(filtersData, ["present_findings", "tags", "dropdownMenuTags"], {})
  ).flat();

  const absent_findings_selected_dropdown_tags = Object.values(
    get(filtersData, ["absent_findings", "tags", "dropdownMenuTags"], {})
  ).flat();

  const present_findinds_selected_tags = get(
    filtersData,
    ["present_findings", "tags", "tags"],
    []
  );
  const absent_findinds_selected_tags = get(
    filtersData,
    ["absent_findings", "tags", "tags"],
    []
  );

  let filterConfig = get(appConfig, ["filters"], []);
  const filtersList = { ...filterConfig };

  const findingsJoins = ["or", "and"];
  //saveable filters data from profile call
  const filterList = get(userInfo, ["settings", "saved_filters"], []);
  const _savedFilterList = applyFilterOrder(
    filterList?.filter((item) => item.is_default !== true),
    filterOrder
  );
  const savedFilterList = _savedFilterList.map((item) => {
    return { label: item.name, values: item.filter, id: item.id };
  });

  //create new saveable filters
  const addFilter = useAddFilter();
  const deleteFilter = useDeleteFilter();
  const editFilter = useEditFilter();

  const currentSearch = get(filters, ["data", "search"], "");
  const sources = get(userInfo, ["accessible_sources"], []);

  // Create user options and label mappings from default workspace

  const { data: taskLabelsResponse } = useGetTaskLabels(workspaceId);
  const taskLabels = useMemo(() => taskLabelsResponse || [], [taskLabelsResponse]);

  const [taskLabelOptions, taskLabelMappings] = useMemo(() => {
    const mappings = taskLabels.reduce((result, label) => {
      result[label.id] = label.name;
      return result;
    }, {});
    return [
      ["-1", ...Object.keys(mappings)],
      { "-1": t("label.no_type_tasks", "Include tasks with no type"), ...mappings },
    ];
  }, [taskLabels]);

  const [taskOwnerOptions, taskOwnerLabelMappings] = useMemo(() => {
    const userlist = get(assignableUserList, "users", []);
    const mappings = userlist.reduce((result, user) => {
      result[String(user.id)] = user.name;
      return result;
    }, {});
    return [
      Object.keys(mappings).sort((a, b) =>
        compLettersBeforeNumbers(mappings[a], mappings[b])
      ),
      mappings,
    ];
  }, [assignableUserList]);

  const sitesOptions = useMemo(() => {
    if (!isEmpty(sources)) {
      return sources?.reduce((acc, s) => {
        acc[s.uuid] = s.name;
        return acc;
      }, {});
    }
    return {};
  }, [sources]);

  // Extract LC-specific filter options from workspace tags endpoint
  const programTypeOptions = useMemo(() => {
    if (!isLC || !workspaceTags || !Array.isArray(workspaceTags)) return [];

    const programTypeTag = workspaceTags.find(
      (tag) => tag?.additional?.identifier === "program_type"
    );

    if (!programTypeTag?.properties?.options) {
      return [];
    }

    const rawOptions = programTypeTag.properties.options;
    if (!Array.isArray(rawOptions)) {
      return [];
    }

    return rawOptions.map((opt) => {
      const value = typeof opt === "string" ? opt : opt?.id || opt?.value || String(opt);
      const label =
        typeof opt === "string"
          ? opt
          : opt?.choice || opt?.label || opt?.value || String(opt);
      return { value, label };
    });
  }, [workspaceTags, isLC]);

  const aiRecommendationOptions = useMemo(() => {
    if (!isLC || !workspaceTags || !Array.isArray(workspaceTags)) return [];

    const aiRecommendationTag = workspaceTags.find(
      (tag) => tag?.additional?.identifier === "ai_recommendation"
    );

    if (!aiRecommendationTag?.properties?.options) {
      return [];
    }

    const rawOptions = aiRecommendationTag.properties.options;
    if (!Array.isArray(rawOptions)) {
      return [];
    }

    return rawOptions.map((opt) => {
      const value = typeof opt === "string" ? opt : opt?.id || opt?.value || String(opt);
      const label =
        typeof opt === "string"
          ? opt
          : opt?.choice || opt?.label || opt?.value || String(opt);
      return { value, label };
    });
  }, [workspaceTags, isLC]);

  const manualReviewOptions = useMemo(() => {
    if (!isLC || !workspaceTags || !Array.isArray(workspaceTags)) return [];

    const manualReviewTag = workspaceTags.find(
      (tag) => tag?.additional?.identifier === "manual_review"
    );

    if (!manualReviewTag?.properties?.options) {
      return [];
    }

    const rawOptions = manualReviewTag.properties.options;
    if (!Array.isArray(rawOptions)) {
      return [];
    }

    return rawOptions.map((opt) => {
      const value = typeof opt === "string" ? opt : opt?.id || opt?.value || String(opt);
      const label =
        typeof opt === "string"
          ? opt
          : opt?.choice || opt?.label || opt?.value || String(opt);
      return { value, label };
    });
  }, [workspaceTags, isLC]);

  const programStatusOptions = useMemo(() => {
    if (!isLC || !workspaceTags || !Array.isArray(workspaceTags)) return [];

    const programStatusTag = workspaceTags.find(
      (tag) => tag?.additional?.identifier === "program_status"
    );

    if (!programStatusTag?.properties?.options) {
      return [];
    }

    const rawOptions = programStatusTag.properties.options;
    if (!Array.isArray(rawOptions)) {
      return [];
    }

    return rawOptions.map((opt) => {
      const value = typeof opt === "string" ? opt : opt?.id || opt?.value || String(opt);
      const label =
        typeof opt === "string"
          ? opt
          : opt?.choice || opt?.label || opt?.value || String(opt);
      return { value, label };
    });
  }, [workspaceTags, isLC]);

  const reviewOptions = useMemo(() => {
    if (!isLC || !workspaceTags || !Array.isArray(workspaceTags)) return [];

    const reviewTag = workspaceTags.find(
      (tag) => tag?.additional?.identifier === "review"
    );

    if (!reviewTag?.properties?.options) {
      return [];
    }

    const rawOptions = reviewTag.properties.options;
    if (!Array.isArray(rawOptions)) {
      return [];
    }

    return rawOptions.map((opt) => {
      const value = typeof opt === "string" ? opt : opt?.id || opt?.value || String(opt);
      const label =
        typeof opt === "string"
          ? opt
          : opt?.choice || opt?.label || opt?.value || String(opt);
      return { value, label };
    });
  }, [workspaceTags, isLC]);

  // Get IDs of special custom tags (program_type, ai_recommendation, manual_review, program_status, review)
  const specialCustomTagIds = useMemo(() => {
    if (!isLC || !workspaceTags || !Array.isArray(workspaceTags)) {
      return {
        program_type: null,
        ai_recommendation: null,
        manual_review: null,
        program_status: null,
        review: null,
      };
    }

    const programTypeTag = workspaceTags.find(
      (tag) => tag?.additional?.identifier === "program_type"
    );
    const aiRecommendationTag = workspaceTags.find(
      (tag) => tag?.additional?.identifier === "ai_recommendation"
    );
    const manualReviewTag = workspaceTags.find(
      (tag) => tag?.additional?.identifier === "manual_review"
    );
    const programStatusTag = workspaceTags.find(
      (tag) => tag?.additional?.identifier === "program_status"
    );
    const reviewTag = workspaceTags.find(
      (tag) => tag?.additional?.identifier === "review"
    );

    return {
      program_type: programTypeTag?.id || null,
      ai_recommendation: aiRecommendationTag?.id || null,
      manual_review: manualReviewTag?.id || null,
      program_status: programStatusTag?.id || null,
      review: reviewTag?.id || null,
    };
  }, [workspaceTags, isLC]);

  const isDefaultFilter = isFiltersDataEmpty(filtersData);

  useEffect(() => {
    if (hasClickOutside && showFilters && (!isMobile || !currentSearch) && !isMobile) {
      setShowFilters(false);
    }
  }, [hasClickOutside]);

  useEffect(() => {
    if (isMobile) {
      setShowFilters(true);
    }
  }, [isMobile]);

  useEffect(() => {
    const updateWidth = () => {
      if (ref.current?.parentElement) {
        const width = ref.current.parentElement.offsetWidth;
        setParentWidth(width);
      }
    };

    // Initial width calculation
    updateWidth();

    // Use ResizeObserver to watch for parent element size changes
    // This handles collapse/expand transitions automatically
    let resizeObserver = null;
    if (ref.current?.parentElement && typeof ResizeObserver !== "undefined") {
      resizeObserver = new ResizeObserver(() => {
        // Use requestAnimationFrame to ensure we measure after layout
        requestAnimationFrame(() => {
          updateWidth();
        });
      });
      resizeObserver.observe(ref.current.parentElement);
    }

    // Fallback to window resize listener
    window.addEventListener("resize", updateWidth);

    // Also update after a delay to catch transition completion (500ms transition duration)
    const timeoutId = setTimeout(() => {
      requestAnimationFrame(() => {
        updateWidth();
      });
    }, 600);

    return () => {
      if (resizeObserver) {
        resizeObserver.disconnect();
      }
      clearTimeout(timeoutId);
      window.removeEventListener("resize", updateWidth);
    };
  }, [isFullWidthWorklist, isWorklistCollapsed]);

  // Initialize filtersData from savedFilterData on mount/change (for isLC only)
  // This ensures "Filtered by" string works even when modal is closed
  useEffect(() => {
    // Only for LC
    if (!isLC) return;

    // Only initialize if:
    // 1. savedFilterData exists
    // 2. filtersData is empty (initial state)
    // 3. Filters are actually applied (not default filters about to be applied)
    // 4. Haven't initialized yet
    if (
      savedFilterData &&
      isFiltersDataEmpty(filtersData) &&
      filters?.isFilterApplied &&
      !hasInitializedFiltersData.current
    ) {
      // Handle multi-select case
      if (enableNewFilterLayout && filters?.selectedFilters?.length > 0) {
        // Use savedFilterData if it exists (contains merged state)
        setFiltersData(regularizeFilterData(savedFilterData));
      } else {
        // Single-select case: use savedFilterData directly
        const regularisedFilterData = regularizeFilterData(savedFilterData);
        setFiltersData(regularisedFilterData);
      }

      hasInitializedFiltersData.current = true;
    }
  }, [
    savedFilterData,
    filters?.isFilterApplied,
    filters?.selectedFilters,
    isLC,
    enableNewFilterLayout,
  ]);

  useEffect(() => {
    // Handle multiple saved filters for LC multi-select
    // Only initialize filtersData when modal first opens, not when filters?.selectedFilters changes
    if (isLC && enableNewFilterLayout && showFilters) {
      // Skip if modal selection has been initialized (selectedFiltersInModal is the source of truth)
      if (hasInitializedModalSelection.current) {
        return;
      }
      if (
        filters?.selectedFilters &&
        Array.isArray(filters.selectedFilters) &&
        filters.selectedFilters.length > 0
      ) {
        // Scenario 3: Both saved filters AND manual filters are applied
        // savedFilterData contains the full merged state (saved + manual) when both are applied
        // Use savedFilterData directly if it exists, as it already has the complete merged state
        if (savedFilterData) {
          // savedFilterData already contains both saved and manual filters merged
          setFiltersData(regularizeFilterData(savedFilterData));
        } else {
          // Fallback: Only saved filters (no manual filters)
          // Merge saved filters from selectedFilters
          const mergedFilterData = mergeMultipleSavedFilters(filters.selectedFilters);
          if (mergedFilterData) {
            setFiltersData(mergedFilterData);
          }
        }
        return;
      }
    }

    // Handle single saved filter (original behavior)
    // Initialize from savedFilterData when modal first opens
    // BUT: Only initialize if filtersData is in initial state OR it's a saved filter
    // This prevents overwriting manually applied filters when reopening the modal
    if (
      savedFilterData &&
      !(isLC && enableNewFilterLayout && showFilters) &&
      showFilters &&
      !hasInitializedFiltersData.current
    ) {
      // Only initialize if:
      // 1. It's a saved filter (filters.id exists), OR
      // 2. filtersData is in initial state (empty/default)
      // This preserves manually applied filters that are already in filtersData
      if (filters?.id || isFiltersDataEmpty(filtersData)) {
        const regularisedFilterData = regularizeFilterData(savedFilterData);
        setFiltersData(regularisedFilterData);
        hasInitializedFiltersData.current = true;
      } else {
        // For manually applied filters, mark as initialized to prevent future resets
        hasInitializedFiltersData.current = true;
      }
    }

    // Reset initialization flag when modal closes
    if (!showFilters) {
      hasInitializedFiltersData.current = false;
    }
  }, [
    showFilters,
    isLC,
    enableNewFilterLayout,
    filters?.selectedFilters,
    filters?.id,
    savedFilterData,
  ]);

  // Handle deselection of saved filters in multi-select mode
  // Removes properties from deselected filters while preserving properties from remaining filters
  //
  // Edge cases handled:
  // 1. Single saved filter selected/deselected: Remove/add all its properties correctly
  // 2. Multiple saved filters selected: When one is deselected, only remove properties that
  //    don't exist in any remaining selected filters (to preserve properties from other filters)
  // 3. Manual additions: Preserve manual filter additions when saved filters are selected/deselected
  // 4. All filter types: Handles arrays (program_type, task_owner, etc.), nested tags (site,
  //    present_findings, patient_type, etc.), date ranges, custom_tags, and all categories from filtersList
  const handleFilterDeselection = (
    currentFiltersData,
    deselectedFilters,
    remainingSelectedFilters
  ) => {
    let updatedFiltersData = cloneDeep(currentFiltersData);

    if (deselectedFilters.length === 0) {
      return updatedFiltersData;
    }

    // First, get all properties from remaining selected filters (to know what to keep)
    const remainingMergedData =
      remainingSelectedFilters.length > 0
        ? mergeMultipleSavedFilters(remainingSelectedFilters)
        : null;

    deselectedFilters.forEach((deselectedFilter) => {
      if (!deselectedFilter?.values) return;

      const deselectedFilterValues = regularizeFilterData(deselectedFilter.values);

      // Remove array values that don't exist in remaining filters
      const arrayProperties = [
        "program_type",
        "ai_recommendation",
        "manual_review",
        "program_status",
        "review",
        "task_owner",
        "task_status",
        "task_labels",
      ];
      arrayProperties.forEach((prop) => {
        // Only process if the property exists in current filtersData
        if (updatedFiltersData[prop] && Array.isArray(updatedFiltersData[prop])) {
          const remainingValues = remainingMergedData?.[prop] || [];
          const deselectedValues =
            deselectedFilterValues[prop] && Array.isArray(deselectedFilterValues[prop])
              ? deselectedFilterValues[prop]
              : [];

          // Remove values that are in the deselected filter but not in remaining filters
          updatedFiltersData[prop] = updatedFiltersData[prop].filter((val) => {
            // Keep if it's in remaining filters, or if it's not in the deselected filter
            return remainingValues.includes(val) || !deselectedValues.includes(val);
          });
        }
      });

      // Remove custom_tags that don't exist in remaining filters
      if (
        deselectedFilterValues.custom_tags &&
        Array.isArray(deselectedFilterValues.custom_tags) &&
        updatedFiltersData.custom_tags &&
        Array.isArray(updatedFiltersData.custom_tags)
      ) {
        const remainingCustomTags = remainingMergedData?.custom_tags || [];
        updatedFiltersData.custom_tags = updatedFiltersData.custom_tags.filter(
          (currentTag) => {
            const existsInRemaining = remainingCustomTags.some((remainingTag) => {
              return (
                remainingTag.tag_id === currentTag.tag_id &&
                JSON.stringify(remainingTag.value) === JSON.stringify(currentTag.value)
              );
            });
            if (existsInRemaining) return true;
            return !deselectedFilterValues.custom_tags.some((deletedTag) => {
              return (
                deletedTag.tag_id === currentTag.tag_id &&
                JSON.stringify(deletedTag.value) === JSON.stringify(currentTag.value)
              );
            });
          }
        );
      }

      // Handle dateRange - clear if it matches deselected filter's dateRange and not in remaining
      if (
        deselectedFilterValues.dateRange &&
        Array.isArray(deselectedFilterValues.dateRange) &&
        deselectedFilterValues.dateRange[0] &&
        deselectedFilterValues.dateRange[1]
      ) {
        const remainingDateRange = remainingMergedData?.dateRange;
        const matchesDeselected =
          updatedFiltersData.dateRange &&
          Array.isArray(updatedFiltersData.dateRange) &&
          updatedFiltersData.dateRange[0] &&
          updatedFiltersData.dateRange[1] &&
          new Date(updatedFiltersData.dateRange[0]).getTime() ===
            new Date(deselectedFilterValues.dateRange[0]).getTime() &&
          new Date(updatedFiltersData.dateRange[1]).getTime() ===
            new Date(deselectedFilterValues.dateRange[1]).getTime();

        const matchesRemaining =
          remainingDateRange &&
          Array.isArray(remainingDateRange) &&
          remainingDateRange[0] &&
          remainingDateRange[1] &&
          new Date(remainingDateRange[0]).getTime() ===
            new Date(deselectedFilterValues.dateRange[0]).getTime() &&
          new Date(remainingDateRange[1]).getTime() ===
            new Date(deselectedFilterValues.dateRange[1]).getTime();

        if (matchesDeselected && !matchesRemaining) {
          updatedFiltersData.dateRange = [null, null];
        }
      }

      // Handle taskDueDateRange similarly
      if (
        deselectedFilterValues.taskDueDateRange &&
        Array.isArray(deselectedFilterValues.taskDueDateRange) &&
        deselectedFilterValues.taskDueDateRange[0] &&
        deselectedFilterValues.taskDueDateRange[1]
      ) {
        const remainingTaskDueDateRange = remainingMergedData?.taskDueDateRange;
        const matchesDeselected =
          updatedFiltersData.taskDueDateRange &&
          Array.isArray(updatedFiltersData.taskDueDateRange) &&
          updatedFiltersData.taskDueDateRange[0] &&
          updatedFiltersData.taskDueDateRange[1] &&
          new Date(updatedFiltersData.taskDueDateRange[0]).getTime() ===
            new Date(deselectedFilterValues.taskDueDateRange[0]).getTime() &&
          new Date(updatedFiltersData.taskDueDateRange[1]).getTime() ===
            new Date(deselectedFilterValues.taskDueDateRange[1]).getTime();

        const matchesRemaining =
          remainingTaskDueDateRange &&
          Array.isArray(remainingTaskDueDateRange) &&
          remainingTaskDueDateRange[0] &&
          remainingTaskDueDateRange[1] &&
          new Date(remainingTaskDueDateRange[0]).getTime() ===
            new Date(deselectedFilterValues.taskDueDateRange[0]).getTime() &&
          new Date(remainingTaskDueDateRange[1]).getTime() ===
            new Date(deselectedFilterValues.taskDueDateRange[1]).getTime();

        if (matchesDeselected && !matchesRemaining) {
          updatedFiltersData.taskDueDateRange = [null, null];
        }
      }

      // Handle patient_type (demographics) - only remove tags not in remaining filters
      if (
        deselectedFilterValues.patient_type?.tags &&
        updatedFiltersData.patient_type?.tags
      ) {
        const deletedTags = get(
          deselectedFilterValues,
          ["patient_type", "tags", "tags"],
          []
        );
        const deletedDropdownTags = Object.values(
          get(deselectedFilterValues, ["patient_type", "tags", "dropdownMenuTags"], {})
        ).flat();
        const allDeletedTags = [...deletedTags, ...deletedDropdownTags];

        const remainingTags = get(
          remainingMergedData,
          ["patient_type", "tags", "tags"],
          []
        );
        const remainingDropdownTags = Object.values(
          get(remainingMergedData, ["patient_type", "tags", "dropdownMenuTags"], {})
        ).flat();
        const allRemainingTags = [...remainingTags, ...remainingDropdownTags];

        if (
          updatedFiltersData.patient_type.tags.tags &&
          Array.isArray(updatedFiltersData.patient_type.tags.tags)
        ) {
          updatedFiltersData.patient_type.tags.tags =
            updatedFiltersData.patient_type.tags.tags.filter((tag) => {
              return allRemainingTags.includes(tag) || !allDeletedTags.includes(tag);
            });
        }

        if (updatedFiltersData.patient_type.tags.dropdownMenuTags) {
          if (Array.isArray(updatedFiltersData.patient_type.tags.dropdownMenuTags)) {
            updatedFiltersData.patient_type.tags.dropdownMenuTags =
              updatedFiltersData.patient_type.tags.dropdownMenuTags.filter(
                (tag) => allRemainingTags.includes(tag) || !allDeletedTags.includes(tag)
              );
          } else if (
            typeof updatedFiltersData.patient_type.tags.dropdownMenuTags === "object"
          ) {
            Object.keys(updatedFiltersData.patient_type.tags.dropdownMenuTags).forEach(
              (key) => {
                if (
                  Array.isArray(
                    updatedFiltersData.patient_type.tags.dropdownMenuTags[key]
                  )
                ) {
                  updatedFiltersData.patient_type.tags.dropdownMenuTags[key] =
                    updatedFiltersData.patient_type.tags.dropdownMenuTags[key].filter(
                      (tag) =>
                        allRemainingTags.includes(tag) || !allDeletedTags.includes(tag)
                    );
                  if (
                    updatedFiltersData.patient_type.tags.dropdownMenuTags[key].length ===
                    0
                  ) {
                    delete updatedFiltersData.patient_type.tags.dropdownMenuTags[key];
                  }
                }
              }
            );
          }
        }
      }

      // Handle all other filter categories with tags structure
      Object.keys(filtersList).forEach((filterKey) => {
        const categoryObj = filtersList[filterKey];
        const category = Object.keys(categoryObj)?.[0];

        if (
          !category ||
          category === "dateRange" ||
          category === "taskDueDateRange" ||
          category === "custom_tags" ||
          category === "task_status" ||
          category === "task_owner" ||
          category === "task_labels" ||
          category === "patient_type"
        ) {
          return;
        }

        if (
          deselectedFilterValues[category]?.tags &&
          updatedFiltersData[category]?.tags
        ) {
          const deletedTags = get(deselectedFilterValues, [category, "tags", "tags"], []);
          const deletedDropdownMenuTags = get(
            deselectedFilterValues,
            [category, "tags", "dropdownMenuTags"],
            {}
          );
          const deletedDropdownTags = get(
            deselectedFilterValues,
            [category, "tags", "dropdownTags"],
            []
          );

          // Get remaining tags from remaining filters
          const remainingTags = get(remainingMergedData, [category, "tags", "tags"], []);
          const remainingDropdownMenuTags = get(
            remainingMergedData,
            [category, "tags", "dropdownMenuTags"],
            {}
          );
          const remainingDropdownTagsArray =
            category === "present_findings" || category === "absent_findings"
              ? Object.values(remainingDropdownMenuTags).flat()
              : Array.isArray(remainingDropdownMenuTags)
              ? remainingDropdownMenuTags
              : [];
          const allRemainingTags = [...remainingTags, ...remainingDropdownTagsArray];

          // Handle tags array - only remove tags not in remaining filters
          if (
            updatedFiltersData[category].tags.tags &&
            Array.isArray(updatedFiltersData[category].tags.tags)
          ) {
            updatedFiltersData[category].tags.tags = updatedFiltersData[
              category
            ].tags.tags.filter(
              (tag) => allRemainingTags.includes(tag) || !deletedTags.includes(tag)
            );
          }

          // Handle dropdownMenuTags - only remove tags not in remaining filters
          if (
            deletedDropdownMenuTags &&
            updatedFiltersData[category].tags.dropdownMenuTags
          ) {
            if (category === "present_findings" || category === "absent_findings") {
              if (
                typeof deletedDropdownMenuTags === "object" &&
                !Array.isArray(deletedDropdownMenuTags)
              ) {
                const remainingSections = get(
                  remainingMergedData,
                  [category, "tags", "dropdownMenuTags"],
                  {}
                );
                Object.keys(deletedDropdownMenuTags).forEach((sectionKey) => {
                  const deletedSection = deletedDropdownMenuTags[sectionKey];
                  const remainingSection = remainingSections[sectionKey] || [];
                  if (
                    Array.isArray(deletedSection) &&
                    updatedFiltersData[category].tags.dropdownMenuTags[sectionKey]
                  ) {
                    updatedFiltersData[category].tags.dropdownMenuTags[sectionKey] =
                      updatedFiltersData[category].tags.dropdownMenuTags[
                        sectionKey
                      ].filter(
                        (tag) =>
                          remainingSection.includes(tag) || !deletedSection.includes(tag)
                      );
                    if (
                      updatedFiltersData[category].tags.dropdownMenuTags[sectionKey]
                        .length === 0
                    ) {
                      delete updatedFiltersData[category].tags.dropdownMenuTags[
                        sectionKey
                      ];
                    }
                  }
                });
              }
            } else {
              const deletedArray = Array.isArray(deletedDropdownMenuTags)
                ? deletedDropdownMenuTags
                : [];
              const remainingArray = Array.isArray(remainingDropdownMenuTags)
                ? remainingDropdownMenuTags
                : [];
              if (Array.isArray(updatedFiltersData[category].tags.dropdownMenuTags)) {
                updatedFiltersData[category].tags.dropdownMenuTags = updatedFiltersData[
                  category
                ].tags.dropdownMenuTags.filter(
                  (tag) => remainingArray.includes(tag) || !deletedArray.includes(tag)
                );
              }
            }
          }

          // Handle dropdownTags
          if (
            Array.isArray(deletedDropdownTags) &&
            deletedDropdownTags.length > 0 &&
            updatedFiltersData[category].tags.dropdownTags &&
            Array.isArray(updatedFiltersData[category].tags.dropdownTags)
          ) {
            const remainingDropdownTags = get(
              remainingMergedData,
              [category, "tags", "dropdownTags"],
              []
            );
            updatedFiltersData[category].tags.dropdownTags = updatedFiltersData[
              category
            ].tags.dropdownTags.filter((tagObj) => {
              // Check if this tagObj exists in remaining filters
              const existsInRemaining = remainingDropdownTags.some((remainingTagObj) => {
                const remainingKey = Object.keys(remainingTagObj)[0];
                const currentKey = Object.keys(tagObj)[0];
                if (remainingKey === currentKey) {
                  const remainingValues = remainingTagObj[remainingKey];
                  const currentValues = tagObj[currentKey];
                  return (
                    Array.isArray(remainingValues) &&
                    Array.isArray(currentValues) &&
                    remainingValues.every((val) => currentValues.includes(val))
                  );
                }
                return false;
              });
              if (existsInRemaining) return true;

              return !deletedDropdownTags.some((deletedTagObj) => {
                const deletedKey = Object.keys(deletedTagObj)[0];
                const currentKey = Object.keys(tagObj)[0];
                if (deletedKey === currentKey) {
                  const deletedValues = deletedTagObj[deletedKey];
                  const currentValues = tagObj[currentKey];
                  return (
                    Array.isArray(deletedValues) &&
                    Array.isArray(currentValues) &&
                    deletedValues.every((val) => currentValues.includes(val))
                  );
                }
                return false;
              });
            });
          }
        }
      });
    });

    return updatedFiltersData;
  };

  // Merge remaining selected filters with current filtersData (preserves manual additions)
  const mergeRemainingFilters = (baseFiltersData, remainingSelectedFilters) => {
    if (remainingSelectedFilters.length === 0) {
      return baseFiltersData;
    }

    const mergedFilterData = mergeMultipleSavedFilters(remainingSelectedFilters);
    if (!mergedFilterData) {
      return baseFiltersData;
    }

    const baseData = cloneDeep(baseFiltersData);

    // Merge custom_tags by tag_id (union values), not as a flat set of objects.
    const incomingCustomTags = get(mergedFilterData, ["custom_tags"], []);
    if (Array.isArray(incomingCustomTags) && incomingCustomTags.length > 0) {
      baseData.custom_tags = mergeCustomTagsByTagId(
        get(baseData, ["custom_tags"], []),
        incomingCustomTags
      );
    }

    // Merge arrays (union)
    const arrayFields = [
      "task_status",
      "task_owner",
      "task_labels",
      "program_type",
      "ai_recommendation",
      "manual_review",
      "program_status",
      "review",
    ];
    arrayFields.forEach((field) => {
      const currentValues = get(mergedFilterData, [field], []);
      const baseValues = get(baseData, [field], []);
      if (Array.isArray(currentValues) && currentValues.length > 0) {
        baseData[field] = [...new Set([...baseValues, ...currentValues])];
      }
    });

    // Merge patient_type (demographics)
    const currentPatientType = get(mergedFilterData, ["patient_type"], null);
    const basePatientType = get(baseData, ["patient_type"], null);
    if (currentPatientType && basePatientType) {
      const currentTags = get(currentPatientType, ["tags", "tags"], []);
      const baseTags = get(basePatientType, ["tags", "tags"], []);
      if (Array.isArray(currentTags) && currentTags.length > 0) {
        baseData.patient_type.tags.tags = [...new Set([...baseTags, ...currentTags])];
      }

      const currentDropdownTags = get(
        currentPatientType,
        ["tags", "dropdownMenuTags"],
        []
      );
      const baseDropdownTags = get(basePatientType, ["tags", "dropdownMenuTags"], []);
      if (Array.isArray(currentDropdownTags) && currentDropdownTags.length > 0) {
        if (Array.isArray(baseDropdownTags)) {
          baseData.patient_type.tags.dropdownMenuTags = [
            ...new Set([...baseDropdownTags, ...currentDropdownTags]),
          ];
        } else {
          baseData.patient_type.tags.dropdownMenuTags = currentDropdownTags;
        }
      }
    }

    // Merge date ranges (intersection)
    if (mergedFilterData.dateRange?.[0] && mergedFilterData.dateRange?.[1]) {
      if (!baseData.dateRange?.[0] || !baseData.dateRange?.[1]) {
        baseData.dateRange = mergedFilterData.dateRange;
      } else {
        const mergedStart = new Date(
          Math.max(
            new Date(baseData.dateRange[0]).getTime(),
            new Date(mergedFilterData.dateRange[0]).getTime()
          )
        );
        const mergedEnd = new Date(
          Math.min(
            new Date(baseData.dateRange[1]).getTime(),
            new Date(mergedFilterData.dateRange[1]).getTime()
          )
        );
        if (mergedStart <= mergedEnd) {
          baseData.dateRange = [mergedStart, mergedEnd];
        }
      }
    }

    // Merge taskDueDateRange
    if (
      mergedFilterData.taskDueDateRange?.[0] &&
      mergedFilterData.taskDueDateRange?.[1]
    ) {
      if (!baseData.taskDueDateRange?.[0] || !baseData.taskDueDateRange?.[1]) {
        baseData.taskDueDateRange = mergedFilterData.taskDueDateRange;
      } else {
        const mergedStart = new Date(
          Math.max(
            new Date(baseData.taskDueDateRange[0]).getTime(),
            new Date(mergedFilterData.taskDueDateRange[0]).getTime()
          )
        );
        const mergedEnd = new Date(
          Math.min(
            new Date(baseData.taskDueDateRange[1]).getTime(),
            new Date(mergedFilterData.taskDueDateRange[1]).getTime()
          )
        );
        if (mergedStart <= mergedEnd) {
          baseData.taskDueDateRange = [mergedStart, mergedEnd];
        }
      }
    }

    // Merge all other filter categories with tags structure
    Object.keys(filtersList).forEach((filterKey) => {
      const categoryObj = filtersList[filterKey];
      const category = Object.keys(categoryObj)?.[0];

      if (
        !category ||
        category === "dateRange" ||
        category === "taskDueDateRange" ||
        category === "custom_tags" ||
        category === "task_status" ||
        category === "task_owner" ||
        category === "task_labels" ||
        category === "patient_type"
      ) {
        return;
      }

      const currentCategory = get(mergedFilterData, [category], null);
      const baseCategory = get(baseData, [category], null);

      if (currentCategory && baseCategory) {
        const currentTags = get(currentCategory, ["tags", "tags"], []);
        const baseTags = get(baseCategory, ["tags", "tags"], []);
        if (Array.isArray(currentTags) && currentTags.length > 0) {
          baseData[category].tags.tags = [...new Set([...baseTags, ...currentTags])];
        }

        const currentDropdownMenuTags = get(
          currentCategory,
          ["tags", "dropdownMenuTags"],
          {}
        );
        const baseDropdownMenuTags = get(baseCategory, ["tags", "dropdownMenuTags"], {});

        if (category === "present_findings" || category === "absent_findings") {
          const allSections = new Set([
            ...Object.keys(baseDropdownMenuTags),
            ...Object.keys(currentDropdownMenuTags),
          ]);

          baseData[category].tags.dropdownMenuTags = {};
          allSections.forEach((sectionKey) => {
            const baseSection = get(baseDropdownMenuTags, [sectionKey], []);
            const currentSection = get(currentDropdownMenuTags, [sectionKey], []);
            const combined = [...new Set([...baseSection, ...currentSection])];
            if (combined.length > 0) {
              baseData[category].tags.dropdownMenuTags[sectionKey] = combined;
            }
          });
        } else {
          const baseArray = Array.isArray(baseDropdownMenuTags)
            ? baseDropdownMenuTags
            : [];
          const currentArray = Array.isArray(currentDropdownMenuTags)
            ? currentDropdownMenuTags
            : [];
          if (currentArray.length > 0 || baseArray.length > 0) {
            baseData[category].tags.dropdownMenuTags = [
              ...new Set([...baseArray, ...currentArray]),
            ];
          }
        }

        const currentDropdownTags = get(currentCategory, ["tags", "dropdownTags"], []);
        const baseDropdownTags = get(baseCategory, ["tags", "dropdownTags"], []);
        if (Array.isArray(currentDropdownTags) && currentDropdownTags.length > 0) {
          const mergedDropdownTagsMap = new Map();
          [...baseDropdownTags, ...currentDropdownTags].forEach((tagObj) => {
            const key = Object.keys(tagObj)[0];
            if (key) {
              if (!mergedDropdownTagsMap.has(key)) {
                mergedDropdownTagsMap.set(key, []);
              }
              const existing = mergedDropdownTagsMap.get(key);
              const newValues = tagObj[key];
              mergedDropdownTagsMap.set(key, [...new Set([...existing, ...newValues])]);
            }
          });
          baseData[category].tags.dropdownTags = Array.from(
            mergedDropdownTagsMap.entries()
          ).map(([key, values]) => ({ [key]: values }));
        }
      } else if (currentCategory && !baseCategory) {
        baseData[category] = cloneDeep(currentCategory);
      }
    });

    return baseData;
  };

  // Update filtersData when selectedFiltersInModal changes in multi-select mode
  // This handles real-time updates when filters are selected/deselected in the modal
  useEffect(() => {
    if (isLC && enableNewFilterLayout && showFilters) {
      // Only update after modal selection has been initialized
      if (!hasInitializedModalSelection.current) {
        return;
      }

      // Get the previous selectedFiltersInModal to detect what was deselected
      const currentSelectedIds = selectedFiltersInModal.map((f) => f.id);
      const previousSelectedIds = previousSelectedFiltersInModal.current.map((f) => f.id);
      const deselectedIds = previousSelectedIds.filter(
        (id) => !currentSelectedIds.includes(id)
      );
      const newlySelectedIds = currentSelectedIds.filter(
        (id) => !previousSelectedIds.includes(id)
      );

      // Start with current filtersData (preserves manual additions)
      let updatedFiltersData = cloneDeep(filtersData);

      // If a filter was deselected, remove its properties that don't exist in remaining filters
      // This must happen even when selectedFiltersInModal.length === 0 (last filter deselected)
      if (deselectedIds.length > 0) {
        // Find the deselected filters from the previous modal state (not filters?.selectedFilters which may be stale)
        const deselectedFilters = deselectedIds
          .map((id) => previousSelectedFiltersInModal.current.find((f) => f.id === id))
          .filter(Boolean);

        // Remove deselected filter properties
        updatedFiltersData = handleFilterDeselection(
          updatedFiltersData,
          deselectedFilters,
          selectedFiltersInModal
        );
      }

      if (selectedFiltersInModal.length > 0) {
        // Merge remaining selected filters back in (preserves manual additions)
        updatedFiltersData = mergeRemainingFilters(
          updatedFiltersData,
          selectedFiltersInModal
        );

        setFiltersData(updatedFiltersData);
      } else {
        // No remaining saved filters, but we've already removed deselected filter properties above
        // Only reset to initial state if filters are not currently applied
        // This prevents resetting when modal opens with manually applied filters
        if (!filters?.isFilterApplied) {
          setFiltersData(cloneDeep(INITIAL_FILTERS_CONFIG));
        } else {
          // Preserve manual additions by setting the updated filtersData
          setFiltersData(updatedFiltersData);
        }
      }

      // Update the ref AFTER processing (so next time we have the correct previous state)
      previousSelectedFiltersInModal.current = selectedFiltersInModal;
    }
  }, [
    selectedFiltersInModal,
    isLC,
    enableNewFilterLayout,
    showFilters,
    filters?.isFilterApplied,
    filters?.selectedFilters,
  ]);

  // Sync selectedFiltersInModal when modal opens (for multi-select)
  useEffect(() => {
    if (showFilters && isLC && enableNewFilterLayout) {
      // Only initialize once when modal opens, not on every filter change
      if (!hasInitializedModalSelection.current) {
        // Initialize selection with currently applied filters when opening modal
        if (
          filters?.selectedFilters &&
          Array.isArray(filters.selectedFilters) &&
          filters.selectedFilters.length > 0
        ) {
          setSelectedFiltersInModal(filters.selectedFilters);
        } else if (filters?.id && filters?.isFilterApplied) {
          // Single-select: convert to selectedFiltersInModal format
          // Check both savedFilterList and full filterList to handle default filters
          const filterList = get(userInfo, ["settings", "saved_filters"], []);
          const _savedFilterList = filterList?.filter((item) => item.is_default !== true);
          let singleFilter = _savedFilterList.find((item) => item.id === filters.id);
          // If not found in savedFilterList, check full filterList (for default filters)
          if (!singleFilter) {
            singleFilter = filterList?.find((item) => item.id === filters.id);
          }
          if (singleFilter) {
            setSelectedFiltersInModal([
              { id: singleFilter.id, values: singleFilter.filter },
            ]);
          }
        } else {
          // No filters applied: clear selection
          setSelectedFiltersInModal([]);
        }
        hasInitializedModalSelection.current = true;
      }
    } else {
      // Reset flag when modal closes
      hasInitializedModalSelection.current = false;
    }
  }, [
    showFilters,
    isLC,
    enableNewFilterLayout,
    filters?.id,
    filters?.selectedFilters,
    filters?.isFilterApplied,
    userInfo,
  ]);

  useEffect(() => {
    if (isFiltersDataEmpty(filtersData)) {
      setIsSaveInput(false);
    }
  }, [filtersData]);

  // useEffect(() => {
  //   // Only reset if filtersString is empty AND no filters are currently applied
  //   // This prevents resetting when modal opens and filtersData is being initialized
  //   if (!filtersString && !filters?.isFilterApplied && !filters?.unread_messages) {
  //     setFilters(constants.DEEFAULT_FILTER);
  //     setSavedFilterData(null);
  //     setLastId("");
  //     setSavedFilterName("");
  //     resetFilters();
  //   }
  // }, [filtersString, filters?.isFilterApplied]);

  useEffect(() => {
    if (isLC && selectedWorkspace) {
      setFiltersData((prev) => ({
        ...prev,
        site: {
          tags: {
            dropdownTags: [],
            tags: [],
            dropdownMenuTags: [selectedWorkspace.id],
          },
        },
      }));
    }
  }, [isLC, selectedWorkspace]);

  useEffect(() => {
    if (selectedSingleTag) {
      const _selectedSingleTagVal = {
        ...selectedSingleTag?.values,
        id: selectedSingleTag?.id,
      };
      if (!isEqual(filtersData, _selectedSingleTagVal)) {
        setSelectedSingleTag();
        setSavedFilterName("");
      }
    }
  }, [filtersData, selectedSingleTag]);

  /* regularizeFilterData function regularizes savedFilters data.
    top 3 tags to show for presend and absent findings has changed.
    whith this function irrespective of it finding is coming in `tags` or `dropdownMenuTags`, it will be selected in UI
  */
  const regularizeFilterData = (savedFilterData) => {
    let _filterData = cloneDeep(savedFilterData);
    Object.keys(filtersList).map((filterKey) => {
      const categoryObj = filtersList[filterKey];
      const category = Object.keys(categoryObj)?.[0];
      if (category === "present_findings" || category === "absent_findings") {
        const _tagsData = get(savedFilterData, [category, "tags", "tags"], []);
        const _dropdownMenuTagsData = Object.values(
          get(savedFilterData, [category, "tags", "dropdownMenuTags"], [])
        ).flat();
        const _dropdownMenuTagsList = Object.values(_dropdownMenuTagsData).flat();
        const currentTags = get(categoryObj, [category, "tags", "tags"], []);
        const currentDropdownMenuTags = get(
          categoryObj,
          [category, "tags", "dropdownMenuTags"],
          {}
        );

        let currentCategory = {
          join: get(savedFilterData, [category, "join"], ""),
          tags: {
            ...get(categoryObj, [category, "tags", "tags"], {}),
            tags: currentTags.filter(
              (tag) => _tagsData.includes(tag) || _dropdownMenuTagsList.includes(tag)
            ),
            dropdownMenuTags: Object.keys(currentDropdownMenuTags).reduce(
              (prev, sectionKey) => {
                const section = get(currentDropdownMenuTags, [sectionKey], []).filter(
                  (tag) =>
                    !currentTags.includes(tag) &&
                    (_tagsData.includes(tag) || _dropdownMenuTagsList.includes(tag))
                );
                if (!isEmpty(section)) {
                  prev[sectionKey] = section;
                }
                return prev;
              },
              {}
            ),
          },
        };

        _filterData[category] = currentCategory;
      }
    });

    // Parse reminders structure and extract task filter values (taskDueDateRange, task_status, task_owner, task_labels)
    const reminders = get(savedFilterData, ["reminders"], null);
    const hasReminderFilter = reminders && Object.keys(reminders).length > 0;
    if (hasReminderFilter) {
      const taskFilters = parseRemindersToTaskFilters(reminders);
      _filterData.taskDueDateRange = taskFilters.taskDueDateRange;
      _filterData.task_status = taskFilters.task_status;
      _filterData.task_owner = taskFilters.task_owner;
      _filterData.task_labels = taskFilters.task_labels;
    }

    // Drop the raw `reminders` key once it has been parsed into the live task fields above.
    // Those task fields (taskDueDateRange/task_status/task_owner/task_labels) are now the
    // single source of truth in the modal, and reminders are rebuilt from them at apply time
    // (see buildReminders in formatFiltersObj). Keeping the stale `reminders` here would let a
    // later re-regularize (e.g. reopening the modal) re-parse it and wipe edits such as a newly
    // selected task due date that aren't reflected in the original saved `reminders`.
    delete _filterData.reminders;

    // Ensure custom_tags is always an array.
    // program_type, ai_recommendation, manual_review, program_status, review stay inside custom_tags (no mapping to top-level).
    if (!_filterData.custom_tags || !Array.isArray(_filterData.custom_tags)) {
      _filterData.custom_tags = [];
    }

    return _filterData;
  };

  /* mergeMultipleSavedFilters function merges multiple saved filter objects for LC multi-select. */
  const mergeMultipleSavedFilters = (selectedFiltersArray) => {
    if (
      !selectedFiltersArray ||
      !Array.isArray(selectedFiltersArray) ||
      selectedFiltersArray.length === 0
    ) {
      return null;
    }

    // Regularize each filter first
    const regularizedFilters = selectedFiltersArray
      .map((filter) => {
        if (filter.values) {
          return regularizeFilterData(filter.values);
        }
        return null;
      })
      .filter(Boolean);

    if (regularizedFilters.length === 0) {
      return null;
    }

    // Start with the first filter as base
    let mergedFilter = cloneDeep(regularizedFilters[0]);

    // Merge remaining filters
    for (let i = 1; i < regularizedFilters.length; i++) {
      const currentFilter = regularizedFilters[i];

      // Merge date ranges - take the intersection (overlapping range)
      if (currentFilter.dateRange?.[0] && currentFilter.dateRange?.[1]) {
        if (!mergedFilter.dateRange?.[0] || !mergedFilter.dateRange?.[1]) {
          mergedFilter.dateRange = currentFilter.dateRange;
        } else {
          const mergedStart = new Date(
            Math.max(
              new Date(mergedFilter.dateRange[0]).getTime(),
              new Date(currentFilter.dateRange[0]).getTime()
            )
          );
          const mergedEnd = new Date(
            Math.min(
              new Date(mergedFilter.dateRange[1]).getTime(),
              new Date(currentFilter.dateRange[1]).getTime()
            )
          );
          if (mergedStart <= mergedEnd) {
            mergedFilter.dateRange = [mergedStart, mergedEnd];
          }
        }
      }

      // Merge task due date ranges
      if (currentFilter.taskDueDateRange?.[0] && currentFilter.taskDueDateRange?.[1]) {
        if (!mergedFilter.taskDueDateRange?.[0] || !mergedFilter.taskDueDateRange?.[1]) {
          mergedFilter.taskDueDateRange = currentFilter.taskDueDateRange;
        } else {
          const mergedStart = new Date(
            Math.max(
              new Date(mergedFilter.taskDueDateRange[0]).getTime(),
              new Date(currentFilter.taskDueDateRange[0]).getTime()
            )
          );
          const mergedEnd = new Date(
            Math.min(
              new Date(mergedFilter.taskDueDateRange[1]).getTime(),
              new Date(currentFilter.taskDueDateRange[1]).getTime()
            )
          );
          if (mergedStart <= mergedEnd) {
            mergedFilter.taskDueDateRange = [mergedStart, mergedEnd];
          }
        }
      }

      // Merge custom_tags by tag_id (union values), not as a flat set of objects.
      const currentCustomTags = get(currentFilter, ["custom_tags"], []);
      if (Array.isArray(currentCustomTags) && currentCustomTags.length > 0) {
        mergedFilter.custom_tags = mergeCustomTagsByTagId(
          get(mergedFilter, ["custom_tags"], []),
          currentCustomTags
        );
      }

      // Merge arrays
      const arrayFields = [
        "task_status",
        "task_owner",
        "task_labels",
        "program_type",
        "ai_recommendation",
        "manual_review",
        "program_status",
        "review",
      ];
      arrayFields.forEach((field) => {
        const currentValues = get(currentFilter, [field], []);
        const mergedValues = get(mergedFilter, [field], []);
        if (Array.isArray(currentValues) && currentValues.length > 0) {
          mergedFilter[field] = [...new Set([...mergedValues, ...currentValues])];
        }
      });

      // Merge filter categories with tags structure
      Object.keys(filtersList).forEach((filterKey) => {
        const categoryObj = filtersList[filterKey];
        const category = Object.keys(categoryObj)?.[0];

        if (
          !category ||
          category === "dateRange" ||
          category === "taskDueDateRange" ||
          category === "custom_tags" ||
          category === "task_status" ||
          category === "task_owner" ||
          category === "task_labels"
        ) {
          return;
        }

        const currentCategory = get(currentFilter, [category], null);
        const mergedCategory = get(mergedFilter, [category], null);

        if (currentCategory && mergedCategory) {
          // Merge tags arrays (union)
          const currentTags = get(currentCategory, ["tags", "tags"], []);
          const mergedTags = get(mergedCategory, ["tags", "tags"], []);
          if (Array.isArray(currentTags) && currentTags.length > 0) {
            mergedFilter[category].tags.tags = [
              ...new Set([...mergedTags, ...currentTags]),
            ];
          }

          // Merge dropdownMenuTags
          const currentDropdownMenuTags = get(
            currentCategory,
            ["tags", "dropdownMenuTags"],
            {}
          );
          const mergedDropdownMenuTags = get(
            mergedCategory,
            ["tags", "dropdownMenuTags"],
            {}
          );

          if (category === "present_findings" || category === "absent_findings") {
            // For findings, merge by section (union within each section)
            const allSections = new Set([
              ...Object.keys(mergedDropdownMenuTags),
              ...Object.keys(currentDropdownMenuTags),
            ]);

            mergedFilter[category].tags.dropdownMenuTags = {};
            allSections.forEach((sectionKey) => {
              const mergedSection = get(mergedDropdownMenuTags, [sectionKey], []);
              const currentSection = get(currentDropdownMenuTags, [sectionKey], []);
              const combined = [...new Set([...mergedSection, ...currentSection])];
              if (combined.length > 0) {
                mergedFilter[category].tags.dropdownMenuTags[sectionKey] = combined;
              }
            });

            // Preserve join logic from first filter (or use 'or' as default)
            if (!mergedFilter[category].join) {
              mergedFilter[category].join = get(currentCategory, ["join"], "or");
            }
          } else {
            // For other categories, merge as arrays (union)
            const mergedArray = Array.isArray(mergedDropdownMenuTags)
              ? mergedDropdownMenuTags
              : [];
            const currentArray = Array.isArray(currentDropdownMenuTags)
              ? currentDropdownMenuTags
              : [];
            if (currentArray.length > 0 || mergedArray.length > 0) {
              mergedFilter[category].tags.dropdownMenuTags = [
                ...new Set([...mergedArray, ...currentArray]),
              ];
            }
          }

          // Merge dropdownTags if they exist
          const currentDropdownTags = get(currentCategory, ["tags", "dropdownTags"], []);
          const mergedDropdownTags = get(mergedCategory, ["tags", "dropdownTags"], []);
          if (Array.isArray(currentDropdownTags) && currentDropdownTags.length > 0) {
            // Merge dropdownTags objects by their keys
            const mergedDropdownTagsMap = new Map();
            [...mergedDropdownTags, ...currentDropdownTags].forEach((tagObj) => {
              const key = Object.keys(tagObj)[0];
              if (key) {
                if (!mergedDropdownTagsMap.has(key)) {
                  mergedDropdownTagsMap.set(key, []);
                }
                const existing = mergedDropdownTagsMap.get(key);
                const newValues = tagObj[key];
                mergedDropdownTagsMap.set(key, [...new Set([...existing, ...newValues])]);
              }
            });
            mergedFilter[category].tags.dropdownTags = Array.from(
              mergedDropdownTagsMap.entries()
            ).map(([key, values]) => ({ [key]: values }));
          }
        } else if (currentCategory && !mergedCategory) {
          // If merged doesn't have this category but current does, add it
          mergedFilter[category] = cloneDeep(currentCategory);
        }
      });
    }

    return mergedFilter;
  };

  // Remove workspace/site tags from dropdownMenuTags
  const stripWorkspaceSite = (data) => {
    const workspaceTagIds = [selectedWorkspace?.id, selectedWorkspace?.value]
      .filter((id) => id !== undefined && id !== null)
      .map((id) => String(id));

    const menuTags = get(data, ["site", "tags", "dropdownMenuTags"], []);
    if (!isLC || workspaceTagIds.length === 0 || !menuTags?.length) {
      return data;
    }

    const cleanedMenuTags = menuTags.filter(
      (tag) => !workspaceTagIds.includes(String(tag))
    );
    if (cleanedMenuTags.length === menuTags.length) {
      return data;
    }

    return {
      ...data,
      site: {
        ...data.site,
        tags: {
          ...data.site.tags,
          dropdownMenuTags: cleanedMenuTags,
        },
      },
    };
  };

  // Build filtersString: include saved filter label AND other filter properties
  // Use savedFilterData (applied filters) instead of filtersData (draft) so the string
  // only updates when "Apply" is clicked, not while editing in the modal
  const filtersString = useMemo(() => {
    const isQuickAccessApply =
      isLC &&
      enableNewFilterLayout &&
      !savedFilterData &&
      filters?.isFilterApplied &&
      Array.isArray(filters?.selectedFilters) &&
      filters.selectedFilters.length > 0;

    const filtersDataForString =
      filters?.isFilterApplied && savedFilterData
        ? savedFilterData
        : isQuickAccessApply
        ? mergeMultipleSavedFilters(filters.selectedFilters)
        : filtersData;

    const savedFilterLabel = filters?.label;

    const labelFiltersData = stripWorkspaceSite(filtersDataForString);
    const otherFiltersString = getFilterString(
      labelFiltersData,
      labelMappings,
      filtersList,
      sources,
      taskOwnerLabelMappings,
      workspaceTags,
      isLC ? specialCustomTagIds : null,
      taskLabelMappings
    );

    // Combine saved filter label with other filter properties
    if (savedFilterLabel && otherFiltersString) {
      return `${savedFilterLabel}, ${otherFiltersString}`;
    } else if (savedFilterLabel) {
      return savedFilterLabel;
    } else if (otherFiltersString) {
      return otherFiltersString;
    }
  }, [
    filters?.isFilterApplied,
    savedFilterData,
    filtersData,
    filters?.label,
    labelMappings,
    filtersList,
    sources,
    taskOwnerLabelMappings,
    workspaceTags,
    isLC,
    enableNewFilterLayout,
    filters?.selectedFilters,
    selectedWorkspace?.id,
    selectedWorkspace?.value,
  ]);

  const resetFilters = () => {
    setShowIcon(true);
    const resetState = DEFAULT_SEARCH_FILTERS;
    setFiltersData(resetState);
    setSelectedSingleTag();
    localStorage.removeItem(constants.PERSISTED.filterObj);
    localStorage.removeItem(constants.PERSISTED.savedFilterData);
  };

  const onSaveFilter = async (e) => {
    try {
      // Build reminders structure from filtersData if task filters are present
      const reminders = buildReminders(filtersData);

      // Prepare filter payload with reminders if they exist
      const filterPayload = { ...filtersData };
      if (reminders) {
        filterPayload.reminders = reminders;
      }

      if (selectedSingleTag?.id) {
        const payload = {
          name: savedFilterName,
          filter: filterPayload,
          id: selectedSingleTag?.id,
        };
        await editFilter.mutateAsync(payload);
        setSavedFilterName("");
        refetchProfile();
        setSelectedSingleTag({
          label: savedFilterName,
          values: filterPayload,
          id: selectedSingleTag?.id,
        });
        Toast.success(t("filter.toast.updated_successfully"));
        setIsSaveInput(false);
        trackEvent(MIXPANEL_EVENTS.FILTER_UPDATED, payload);
      } else {
        const payload = {
          name: savedFilterName,
          filter: filterPayload,
        };
        await addFilter.mutateAsync(payload);
        setSavedFilterName("");
        await refetchProfile();
        newFilterAdded.current = savedFilterName;
        Toast.success(t("filter.toast.added_successfully"));
        setIsSaveInput(false);
        trackEvent(MIXPANEL_EVENTS.FILTER_SAVED, payload);
      }
      e.stopPropagation();
    } catch (err) {
      setIsSaveInput(false);
      Toast.error(t("toast.something_went_wrong"));
    }
  };

  useEffect(() => {
    if (newFilterAdded.current) {
      const filter = filterList.find((f) => f.name === newFilterAdded.current);
      if (filter) {
        setSelectedSingleTag({
          label: filter.name,
          values: filter.filter,
          id: filter.id,
        });
      }
      newFilterAdded.current = "";
    }
  }, [newFilterAdded.current, filterList]);

  const applyFilters = (_filtersData, search = currentSearch, clear = false) => {
    const applyFilterChange = () => {
      const filtersAreEmpty = isFiltersDataEmpty(_filtersData);

      // Handle multi-select scenario (isLC && enableNewFilterLayout)
      if (isLC && enableNewFilterLayout && selectedFiltersInModal.length > 0 && !clear) {
        // Get comma-separated filter names for label
        const filterNames = selectedFiltersInModal
          .map((selectedFilter) => {
            const filterItem = savedFilterList.find(
              (item) => item.id === selectedFilter.id
            );
            return filterItem?.label || "";
          })
          .filter((name) => name.length > 0)
          .join(", ");

        const _formattedFilters = formatFiltersObj(
          _filtersData,
          isLC ? null : specialCustomTagIds
        );

        const savedFiltersMerged = mergeMultipleSavedFilters(selectedFiltersInModal);
        const hasManualFilters = savedFiltersMerged
          ? !areFormattedFiltersEqual(
              _filtersData,
              savedFiltersMerged,
              isLC ? null : specialCustomTagIds
            )
          : !isFiltersDataEmpty(_filtersData);

        // Set filters with selectedFilters array (only values) and label
        // Preserve existing filter data (especially search) when applying multiple filters
        const appliedFilters = {
          ...filters, // Preserve existing filter properties
          isFilterApplied: !filtersAreEmpty,
          selectedFilters: selectedFiltersInModal,
          hasManualFilters, // Whether any filters were added beyond the saved filters
          label: filterNames, // This will be displayed in "Filtered By:"
          multiSelectLogic: "AND",
          specialCustomTagIds: specialCustomTagIds, // Store special tag IDs for payload conversion
          data: {
            ..._formattedFilters, // Preserve all existing data properties
            search: search || get(filters, ["data", "search"], ""), // Preserve or use provided search
          },
        };

        // IMPORTANT: Update savedFilterData with the current filtersData (which includes both saved + manual)
        // This ensures when modal reopens, both saved and manual filters are preserved

        setFiltersData(_filtersData);
        setFilters(appliedFilters);
        setSavedFilterData(_filtersData);
        setLastId("");
        setPatientsData(null);
        setShowFilters(false);

        if (appliedFilters) {
          localStorage.setItem(
            constants.PERSISTED.filterObj,
            JSON.stringify(appliedFilters)
          );
          // Also save the merged filtersData to localStorage
          localStorage.setItem(
            constants.PERSISTED.savedFilterData,
            JSON.stringify(_filtersData)
          );
        }

        trackEvent(MIXPANEL_EVENTS.LC_FILTER_APPLIED, {
          filterCount: selectedFiltersInModal.length,
          isMultiSelect: true,
        });

        return;
      }

      // Handle single-select or clear scenario (original behavior)
      setFiltersData(_filtersData);

      // For LC mode, conversion happens later in buildMultiSelectFilterRequest
      // For non-LC mode, filters.data is used directly, so we need conversion here
      const formattedFilters = formatFiltersObj(
        _filtersData,
        isLC ? null : specialCustomTagIds
      );
      const _filters = {
        isFilterApplied: clear ? false : !filtersAreEmpty,
        data: { ...formattedFilters, search: search },
        label: savedFilterName,
        id: _filtersData?.id,
        specialCustomTagIds: specialCustomTagIds, // Store special tag IDs for payload conversion
      };

      setFilters(_filters);
      setLastId("");
      setPatientsData(null);
      setShowFilters(false);
      setSavedFilterData(_filtersData);

      if (_filters && _filtersData) {
        localStorage.setItem(constants.PERSISTED.filterObj, JSON.stringify(_filters));
        localStorage.setItem(
          constants.PERSISTED.savedFilterData,
          JSON.stringify(_filtersData)
        );
      }

      if (formattedFilters?.start_study_date && formattedFilters?.end_study_date) {
        const startDate = startOfDay(new Date(formattedFilters.start_study_date));
        const endDate = startOfDay(new Date(formattedFilters.end_study_date));

        formattedFilters.start_study_date = startDate.toDateString();
        formattedFilters.end_study_date = endDate.toDateString();
        formattedFilters.difference_in_days_study_date =
          differenceInDays(endDate, startDate) + 1;
      }

      trackEvent(MIXPANEL_EVENTS.FILTER_APPLIED, {
        ...formattedFilters,
        isSavedFilter: !!savedFilterName,
        savedFilterName,
        filterId: _filtersData?.id,
        isPromptDate: !!isPromptDate,
      });
    };

    if (clear) {
      applyFilterChange();
      return;
    }

    requestWorklistFilterChange(applyFilterChange);
  };

  useApplyFilterAction(
    applyFilters,
    filtersData,
    onChange,
    patients,
    sitesOptions,
    patientsCount,
    isLoading
  );
  useAddCustomFilters({
    filtersData,
    selectedWorkspaceId: selectedWorkspace?.value,
    applyFilters,
    setFiltersData,
  });

  const setDate = (daysFrom, daysTo, label) => {
    const d = new Date();
    const e = new Date();
    d.setDate(d.getDate() + daysFrom);
    e.setDate(e.getDate() + daysTo);

    setIsPromptDate(true);
    trackEvent(MIXPANEL_EVENTS.FILTER_QUICK_DATE_SELECTED, { type: "scan_date", label });

    setFiltersData({
      ...filtersData,
      dateRange: [d, e],
    });
  };

  // Selects the entire previous calendar month (e.g. on Jun 16 → May 1 to May 31).
  const setLastMonth = () => {
    const now = new Date();
    const start = new Date(now.getFullYear(), now.getMonth() - 1, 1);
    const end = new Date(now.getFullYear(), now.getMonth(), 0);

    setIsPromptDate(true);
    trackEvent(MIXPANEL_EVENTS.FILTER_QUICK_DATE_SELECTED, {
      type: "scan_date",
      label: "last_month",
    });

    setFiltersData({
      ...filtersData,
      dateRange: [start, end],
    });
  };

  const handleDateRangeChange = (value) => {
    if (isPromptDate) {
      setIsPromptDate(false);
    }

    trackEvent(MIXPANEL_EVENTS.FILTER_DATE_RANGE_SET, { type: "scan_date" });
    setFiltersData({
      ...filtersData,
      dateRange: value,
    });
  };

  const handleTagClick = (category, tag) => {
    trackEvent(MIXPANEL_EVENTS.FILTER_TAG_SELECTED, { category, tag });
    const filter_data = {
      ...filtersData,
      [category]: {
        ...filtersData[category],
        tags: {
          ...filtersData[category]?.tags,
          tags: xor([tag], get(filtersData, [category, "tags", "tags"], [])),
        },
      },
    };
    setFiltersData(filter_data);
  };

  const handleDropdownChange = (category, values) => {
    // Fire appropriate Mixpanel event based on category
    const dropdownEventMap = {
      task_owner: MIXPANEL_EVENTS.FILTER_ASSIGNEE_SELECTED,
      task_labels: MIXPANEL_EVENTS.TASK_LABEL_SELECTED,
      site: MIXPANEL_EVENTS.FILTER_WORKSPACE_SELECTED,
      report_status: MIXPANEL_EVENTS.FILTER_REPORT_STATUS_SELECTED,
      present_findings: MIXPANEL_EVENTS.FILTER_FINDINGS_SELECTED,
      absent_findings: MIXPANEL_EVENTS.FILTER_FINDINGS_SELECTED,
    };
    trackEvent(dropdownEventMap[category] || MIXPANEL_EVENTS.FILTER_TAG_SELECTED, {
      category,
      values,
    });

    // For task_owner, store IDs directly as array
    if (category === "task_owner") {
      setFiltersData({
        ...filtersData,
        task_owner: values,
      });
      return;
    }
    if (category === "task_labels") {
      setFiltersData({
        ...filtersData,
        task_labels: values,
      });
      return;
    }

    const filter_data = {
      ...filtersData,
      [category]: {
        ...filtersData[category],
        tags: {
          ...filtersData[category].tags,
          dropdownMenuTags:
            category === "present_findings" || category === "absent_findings"
              ? { ...values }
              : [...values],
        },
      },
    };
    setFiltersData(filter_data);
  };

  const handleFindingsJoinChange = (category, value) => {
    if (category === "present_findings" || category === "absent_findings") {
      trackEvent(MIXPANEL_EVENTS.FILTER_FINDINGS_JOIN_CHANGED, { category, value });
      const filter_data = {
        ...filtersData,
        [category]: {
          ...filtersData[category],
          tags: {
            ...filtersData[category].tags,
          },
          join: value,
        },
      };
      setFiltersData(filter_data);
    }
  };

  const handleSingleTagchange = (label, values, id) => {
    const _values = regularizeFilterData(values);

    // If multi-select mode (isLC && enableNewFilterLayout), toggle selection instead of applying immediately
    if (isLC && enableNewFilterLayout) {
      setSelectedFiltersInModal((prev) => {
        // In modal mode, selectedFiltersInModal is the source of truth for selection state
        const isSelectedInModal = prev.some((filter) => filter.id === id);

        if (isSelectedInModal) {
          // Deselect - remove from selectedFiltersInModal
          return prev.filter((filter) => filter.id !== id);
        } else {
          // Select - add with values property
          return [...prev, { id, values: _values }];
        }
      });
      // Don't apply immediately in multi-select mode
      return;
    }

    // Single-select mode: apply immediately (original behavior)
    setSelectedSingleTag({ label, values: _values, id });
    setSavedFilterName(label);
    setFiltersData({ ..._values, id });
  };

  const handleDropdownTagChange = (category, tagLabel, values) => {
    trackEvent(MIXPANEL_EVENTS.FILTER_LABEL_SELECTED, { category, label: tagLabel });
    setFiltersData((prevFilters) => {
      let isPresent = !isEmpty(
        get(prevFilters, [category, "tags", "dropdownTags"], []).filter(
          (tagObj) => Object.keys(tagObj)[0] === tagLabel
        )
      );

      if (isPresent) {
        let indexToRemove = [];

        const updatedFilters = {
          ...prevFilters,
          [category]: {
            ...prevFilters[category],
            tags: {
              ...get(prevFilters, [category, "tags"]),
              dropdownTags: [
                ...get(prevFilters, [category, "tags", "dropdownTags"], []).map(
                  (tagObj, index) => {
                    if (Object.keys(tagObj)[0] === tagLabel) {
                      if (isEmpty(values)) indexToRemove.push(tagLabel);
                      return { [tagLabel]: [...values] };
                    } else {
                      return tagObj;
                    }
                  }
                ),
              ],
            },
          },
        };
        indexToRemove.map((label) => {
          remove(updatedFilters[category].tags.dropdownTags, (x) => {
            return Object.keys(x)[0] === label;
          });
          remove(updatedFilters.lab_results.tags.dropdownTags, (x) => {
            return Object.keys(x)[0] === label;
          });
        });
        return updatedFilters;
      } else {
        return {
          ...prevFilters,
          [category]: {
            ...prevFilters[category],
            tags: {
              ...get(prevFilters, [category, "tags"]),
              dropdownTags: [
                ...get(prevFilters, [category, "tags", "dropdownTags"]),
                { [tagLabel]: [...values] },
              ],
            },
          },
        };
      }
    });
  };

  const handleDeleteSavedFilter = async (id, label) => {
    try {
      await deleteFilter.mutateAsync(id);
      refetchProfile();
      setSavedFilterName("");

      // Check if the deleted filter is currently applied
      const isSingleFilterApplied = filters?.id === id;
      const isInMultiSelect =
        filters?.selectedFilters &&
        Array.isArray(filters.selectedFilters) &&
        filters.selectedFilters.some((filter) => filter.id === id);

      // If the deleted filter is the only one applied (single filter), clear all filters
      if (isSingleFilterApplied) {
        setFilters({ ...constants.DEEFAULT_FILTER, selectedFilters: undefined });
        setSavedFilterData(null);
        setLastId("");
        if (isLC && enableNewFilterLayout) {
          setSelectedFiltersInModal([]);
        }
        resetFilters();
      }
      // If the deleted filter is part of multi-select, remove only that filter and preserve manual additions
      else if (isInMultiSelect) {
        requestWorklistFilterChange(() => {
          const deletedFilter = filters.selectedFilters.find(
            (filter) => filter.id === id
          );
          const remainingFilters = filters.selectedFilters.filter(
            (filter) => filter.id !== id
          );

          // Remove from selectedFiltersInModal if present
          if (isLC && enableNewFilterLayout) {
            setSelectedFiltersInModal((prev) =>
              prev.filter((filter) => filter.id !== id)
            );
          }

          // Use handleFilterDeselection to remove the deleted filter's properties
          // This preserves properties that exist in remaining filters and manual additions
          let updatedFiltersData = handleFilterDeselection(
            filtersData,
            deletedFilter ? [deletedFilter] : [],
            remainingFilters
          );

          // Merge remaining filters back in (preserves manual additions)
          updatedFiltersData = mergeRemainingFilters(
            updatedFiltersData,
            remainingFilters
          );

          // Get filter names for label
          const filterNames =
            remainingFilters.length > 0
              ? remainingFilters
                  .map(
                    (filter) =>
                      filter.label ||
                      savedFilterList.find((item) => item.id === filter.id)?.name ||
                      ""
                  )
                  .filter((name) => name.length > 0)
                  .join(", ")
              : "";

          // Update state
          setSavedFilterData(updatedFiltersData);
          setFiltersData(updatedFiltersData);

          if (remainingFilters.length > 0) {
            // Re-detect manual additions against the remaining saved filters so the full
            // current-selection object is kept only while such additions exist.
            const remainingMerged = mergeMultipleSavedFilters(remainingFilters);
            const hasManualFilters = remainingMerged
              ? !areFormattedFiltersEqual(
                  updatedFiltersData,
                  remainingMerged,
                  isLC ? null : specialCustomTagIds
                )
              : !isFiltersDataEmpty(updatedFiltersData);
            // Update applied filters with remaining filters
            const updatedFilters = {
              ...filters,
              selectedFilters: remainingFilters,
              label: filterNames,
              hasManualFilters,
              data: {
                ...get(filters, ["data"], {}),
              },
            };

            setTimeout(() => {
              setFilters(updatedFilters);
            }, 100);

            localStorage.setItem(
              constants.PERSISTED.filterObj,
              JSON.stringify(updatedFilters)
            );
            localStorage.setItem(
              constants.PERSISTED.savedFilterData,
              JSON.stringify(updatedFiltersData)
            );
          } else {
            // No saved filters left - check if there are manual additions.
            // For each field in filtersData:
            //   - If savedFilterMerged has it with the same value → came from saved filter → OK
            //   - Otherwise check if it's at its empty initial value → if not, manual addition
            // This handles the case where savedFilterMerged has extra fields (reminders,
            // taskDueDateRange, task_owner) that filtersData doesn't have.
            const savedFilterMerged = deletedFilter
              ? mergeMultipleSavedFilters([deletedFilter])
              : null;
            const hasManualAdditions = Object.entries(filtersData).some(
              ([key, value]) => {
                if (savedFilterMerged && isEqual(savedFilterMerged[key], value))
                  return false;
                const emptyValue = EMPTY_STATE[key];
                return emptyValue !== undefined ? !isEqual(value, emptyValue) : false;
              }
            );

            if (hasManualAdditions) {
              // Preserve manual additions
              const manualFilters = {
                ...filters,
                isFilterApplied: true,
                selectedFilters: [],
                label: "",
                id: undefined,
              };

              setTimeout(() => {
                setFilters(manualFilters);
              }, 100);

              localStorage.setItem(
                constants.PERSISTED.filterObj,
                JSON.stringify(manualFilters)
              );
              localStorage.setItem(
                constants.PERSISTED.savedFilterData,
                JSON.stringify(updatedFiltersData)
              );
            } else {
              // Clear everything
              setFilters(constants.DEEFAULT_FILTER);
              setSavedFilterData(null);
              setLastId("");
              if (isLC && enableNewFilterLayout) {
                setSelectedFiltersInModal([]);
              }
              resetFilters();
            }
          }
        });
      }
      // If the deleted filter is not applied, just remove it from the list (already done via refetchProfile)

      Toast.success(t("filter.toast.removed_successfully"));
      trackEvent(MIXPANEL_EVENTS.FILTER_DELETED, {
        label,
        filterId: id,
      });
    } catch (err) {
      Toast.error(t("toast.something_went_wrong"));
    }
  };

  const handleConfirmDelete = () => {
    if (filterToDelete.id && filterToDelete.label) {
      handleDeleteSavedFilter(filterToDelete.id, filterToDelete.label);
      setIsDeleteModalOpen(false);
      setFilterToDelete({ id: null, label: null });
    }
  };

  const handleCancelDelete = () => {
    trackEvent(MIXPANEL_EVENTS.FILTER_DELETE_CANCELLED);
    setIsDeleteModalOpen(false);
    setFilterToDelete({ id: null, label: null });
  };

  const findingsJoinOptions = findingsJoins.map((val) => {
    const opt = {
      label: null,
      value: val,
      title: t("filter.and_title"),
    };
    let description = t("filter.and_description");
    if (val === "or") {
      opt.title = t("filter.or_title");
      description = t("filter.or_description");
    }
    opt.label = (
      <div className="space-y-4">
        <div>{opt.title}</div>
        <div
          className="text-sm text-gray-400"
          dangerouslySetInnerHTML={{
            __html: description
              .replace(
                "[TB]",
                `<span style='background: #29333e;padding: 6px;border-radius: 9999px;margin-right: 3px;margin-left: 3px;'>
                    Tuberculosis
                  </span>`
              )
              .replace(
                "[NODULE]",
                `<span style='background: #29333e;padding: 6px;border-radius: 9999px;margin-right: 3px;margin-left: 3px;'>
                    Nodule
                  </span>`
              ),
          }}
        ></div>
      </div>
    );

    return opt;
  });

  return (
    <div
      ref={ref}
      className={classNames(
        "flex flex-col h-full sm:h-auto relative px-4 bg-white dark:bg-gray-900 text-gray-800 dark:text-gray-200",
        {
          "!absolute top-3 left-3 z-30 animate-fullWorklist rounded-md":
            isFullWidthWorklist,
          "py-4 animate-shortWorklist": !isFullWidthWorklist,
          "relative overflow-hidden !px-0": isMobile,
          "h-screen": isMobile && showFilters,
          "!p-0": isLC && isBulkActions,
        }
      )}
    >
      <div className={classNames({ "mx-4": isMobile })}>
        <DebounceInput
          value={currentSearch}
          onChange={(val) => {
            // setSearch(val);
            trackEvent(MIXPANEL_EVENTS.PATIENT_SEARCH, { search: val });
            requestWorklistFilterChange(() => {
              onChangeSearch(val);
              if (isMobile) {
                setShowFilters(false);
              }
            });
          }}
          onFocus={(e) => {
            e.target.placeholder = t("filter.search_name_identifier");
            setShowIcon(false);
          }}
          onBlur={(e) => {
            setTimeout(() => {
              e.target.placeholder = t("Search or filter patients");
              if (filters?.isFilterApplied) {
                setShowIcon(false);
              } else {
                setShowIcon(true);
              }
            }, 100);
          }}
          //isPrimary
          placeholder={t("Search or filter patients")}
          className={classNames("rounded-md text-gray-800 w-full z-40", {
            "!w-[350px]": isFullWidthWorklist,
            "border-none": isFullWidthWorklist && !filters?.isFilterApplied,
            "border border-teal-500 shadow-md shadow-teal-500/40":
              filters?.isFilterApplied,
            "border-[1.5px] !border-gray-300 dark:!border-gray-800":
              !filters?.isFilterApplied,
          })}
          LHSIcon={
            isMobile && filters?.isFilterApplied && !currentSearch ? (
              <AwesomeIcon
                name={faBarsFilter}
                size="lg"
                className={classNames(
                  "w-5 absolute !top-1/2 !transform-none -translate-y-1/2 left-5 cursor-pointer text-gray-400 dark:text-gray-600",
                  {
                    "!text-gray-800 dark:!text-gray-200": filters?.isFilterApplied,
                    hidden: !showIcon,
                  }
                )}
              />
            ) : (
              <AwesomeIcon
                name={faMagnifyingGlass}
                size="lg"
                className={classNames(
                  "w-5 absolute !top-1/2 transform-none -translate-y-1/2 left-5 cursor-pointer text-gray-400 dark:text-gray-600",
                  {
                    "!text-gray-800 dark:!text-gray-200": filters?.isFilterApplied,
                    hidden: !showIcon,
                  }
                )}
              />
            )
          }
          RHSIcon={
            <div
              className="text-teal-500 dark:text-gray-400 w-6 h-6 absolute top-1/2 transform-none -translate-y-1/2 right-3 cursor-pointer"
              onClick={() => {
                if (!showFilters) trackEvent(MIXPANEL_EVENTS.FILTER_PANEL_OPENED);
                setShowFilters(!showFilters);
              }}
            >
              <AwesomeIcon
                name={faFilterList}
                size="lg"
                className="text-teal-500 dark:text-teal-400"
                label="filterIcon"
              />
            </div>
          }
          closeIcon={
            <button
              onClick={() => {
                trackEvent(MIXPANEL_EVENTS.MOBILE_SEARCH_CLOSED);
                Store.update((s) => {
                  s.hasMobileSearch = false;
                });
                setFilters({ isFilterApplied: false, data: null, label: "" });
                setSavedFilterData(null);
                localStorage.removeItem(constants.PERSISTED.filterObj);
                localStorage.removeItem(constants.PERSISTED.savedFilterData);
              }}
              className="absolute sm:hidden top-1/2 transform-none -translate-y-1/2 right-8 cursor-pointer"
            >
              <AwesomeIcon name={faXmark} className="text-red-500" />
            </button>
          }
          // hasClear={isMobile}
          inputClassName={classNames(
            "!bg-gray-100 dark:!bg-gray-800 sm:dark:!bg-gray-900 placeholder:text-gray-400 focus:!border focus:!outline-none focus:!ring-teal-500/40 focus:!border-teal-500 focus:!shadow-md focus:!shadow-teal-500/40",
            {
              //"!bg-gray-200": isFullWidthWorklist,
              "!pl-3": !showIcon,
              "placeholder:!text-gray-800 dark:placeholder:!text-gray-200":
                filters?.isFilterApplied,
            }
          )}
        />
      </div>
      <div className="flex justify-between items-center gap-3 mt-2">
        {!isMobile &&
          filters?.isFilterApplied &&
          !isFullWidthWorklist &&
          filtersString && (
            <p
              title={filtersString}
              className="text-gray-600 dark:text-gray-200 text-md py-3 whitespace-nowrap overflow-hidden text-ellipsis cursor-default"
            >
              {t("filtered_by")}: {filtersString}
            </p>
          )}
        {/* save and clear buttons new */}
        {filters?.isFilterApplied && (
          <div className="flex">
            {!isDefaultFilter && !currentSearch && (
              <button
                className={classNames(
                  "text-teal-500 text-md hover:text-teal-300 font-medium uppercase whitespace-nowrap flex-nowrap",
                  {
                    hidden: isFullWidthWorklist || isMobile,
                    "cursor-not-allowed !text-gray-500": !!filters?.id,
                  }
                )}
                onClick={() => {
                  trackEvent(MIXPANEL_EVENTS.FILTER_SAVE_OPENED);
                  setShowFilters(true);
                  setIsSaveInput(true);
                }}
              >
                {t("button.save")}
              </button>
            )}
            <button
              className={classNames("cursor-pointer ml-3", {
                hidden: isFullWidthWorklist || isMobile,
              })}
              onClick={() => {
                trackEvent(MIXPANEL_EVENTS.FILTER_CLEARED);
                setFilters(constants.DEEFAULT_FILTER);
                setSavedFilterData(null);
                setLastId("");
                setSavedFilterName("");
                // Clear selectedFiltersInModal in multi-select mode
                if (isLC && enableNewFilterLayout) {
                  setSelectedFiltersInModal([]);
                }
                resetFilters();
              }}
            >
              <div className="text-teal-500 text-md cursor-pointer hover:text-teal-300 font-medium uppercase whitespace-nowrap flex-nowrap">
                {t("button.clear")}
              </div>
            </button>
          </div>
        )}
      </div>
      <div
        className={classNames("flex justify-between items-center ", {
          "mt-3 w-full": !isFullWidthWorklist,
          "mt-5": isFullWidthWorklist,
          "!mt-0": isLC && isBulkActions,
          hidden:
            isMobile ||
            !filters?.isFilterApplied ||
            !isFullWidthWorklist ||
            filters?.label === constants.DEFAULT_FILTER_NAME.negativeCXR,
        })}
      >
        <div className="py-2 px-4 bg-[#CCE6E6] dark:bg-teal-900 rounded-lg text-gray-800 dark:text-gray-100 w-[350px] mt-0 sm:mt-[6px]">
          <div className="flex justify-between items-center">
            <div className="text-2xl font-medium">
              {isCountLoading ? (
                <div
                  className="border-solid border-white border-2 border-r-transparent animate-spin inline-block w-5 h-5 rounded-full text-blue-600 mr-2"
                  role="status"
                />
              ) : (
                <div className="flex space-x-4 items-start">
                  <p>{patientsCount}</p>
                  <p className="text-[14px]"> {t("patients_key")}</p>
                </div>
              )}
            </div>
            <div className="flex justify-end items-center text-[14px]">
              <div className="flex">
                {!isDefaultFilter && !currentSearch && (
                  <button
                    className={classNames(
                      "text-md font-medium uppercase text-teal-400 hover:text-teal-300",
                      {
                        "cursor-not-allowed !text-gray-500": !!filters?.id,
                      }
                    )}
                    onClick={() => {
                      trackEvent(MIXPANEL_EVENTS.FILTER_SAVE_OPENED);
                      setShowFilters(true);
                      setIsSaveInput(true);
                    }}
                    disabled={!!filters?.id}
                  >
                    {t("button.save")}
                  </button>
                )}
                <button
                  className="text-md font-medium uppercase text-teal-400 hover:text-teal-300 ml-4"
                  onClick={() => {
                    trackEvent(MIXPANEL_EVENTS.FILTER_CLEARED);
                    setFilters(constants.DEEFAULT_FILTER);
                    setSavedFilterData(null);
                    setLastId("");
                    setSavedFilterName("");
                    // Clear selectedFiltersInModal in multi-select mode
                    if (isLC && enableNewFilterLayout) {
                      setSelectedFiltersInModal([]);
                    }
                    resetFilters();
                  }}
                >
                  {t("button.clear")}
                </button>
              </div>
            </div>
          </div>
          <p title={filtersString} className="text-gray-400 h-6 text-[14px] truncate">
            {filtersString}
          </p>
        </div>
      </div>
      <div
        className={classNames(
          `absolute top-1 left-0 flex w-full flex-col !z-30 text-gray-800
           space-y-5 bg-gray-100 dark:bg-gray-900 sm:bg-gray-100 sm:dark:bg-gray-800 sm:border sm:border-gray-700 rounded-lg pt-9
          h-auto sm:max-h-[calc(100vh-109px)]
           `,
          {
            hidden: !showFilters,
            "!-top-3": isFullWidthWorklist,
            "relative overflow-auto !pt-3": isMobile,
            "animate-slideDown ": isMobile && showFilters,
            "sm:!max-h-[calc(100vh-209px)]": isLC,
            "!left-auto !right-0": enableNewFilterLayout && !isMobile && !parentWidth,
            "!z-[900] !pt-4 sm:!w-[400px]":
              (enableNewFilterLayout && parentWidth && !isMobile) ||
              (enableNewFilterLayout && isFullWidthWorklist && !isMobile),
          }
        )}
        style={
          enableNewFilterLayout && !isMobile && (parentWidth || isFullWidthWorklist)
            ? { left: isFullWidthWorklist ? "350px" : `${parentWidth}px` }
            : undefined
        }
      >
        {/* Body */}
        <div className="flex flex-col scrollbar-light h-full space-y-6 overflow-y-auto dark:text-gray-200">
          {!isSaveInput ? (
            <div className="flex items-center justify-between border-y border-gray-800 py-2 sm:border-none sm:py-0 px-5">
              <div className="flex items-center space-x-2">
                <AwesomeIcon
                  name={faUserCircle}
                  size="sm"
                  className="text-gray-600 dark:text-gray-300"
                />
                <div className="flex items-center text-md text-gray-600 dark:text-gray-300">
                  {isCountLoading ? (
                    <div
                      className="border-solid border-white border-2 border-r-transparent animate-spin inline-block w-4 h-4 rounded-full text-blue-600 mr-2"
                      role="status"
                    />
                  ) : (
                    patientsCount
                  )}
                  <div className="ml-2">{t("patients_key")}</div>
                </div>
              </div>
              <div className="flex items-center space-x-5">
                <button
                  disabled={
                    isDefaultFilter || isEqual(selectedSingleTag?.values, filtersData)
                  }
                  onClick={(e) => {
                    trackEvent(MIXPANEL_EVENTS.FILTER_SAVE_OPENED);
                    setIsSaveInput(true);
                    e.stopPropagation();
                  }}
                  className={classNames(
                    "text-md font-medium uppercase text-teal-400 hover:text-teal-300",
                    {
                      "!cursor-not-allowed !text-gray-400": isDefaultFilter,
                      "cursor-not-allowed !text-gray-400": isEqual(
                        selectedSingleTag?.values,
                        filtersData
                      ),
                    }
                  )}
                >
                  {t("button.save")}
                </button>
                <button
                  onClick={() => {
                    trackEvent(MIXPANEL_EVENTS.FILTER_CLEARED);
                    setFilters(constants.DEEFAULT_FILTER);
                    setSavedFilterData(null);
                    setSavedFilterName("");
                    // Clear selectedFiltersInModal in multi-select mode
                    if (isLC && enableNewFilterLayout) {
                      setSelectedFiltersInModal([]);
                    }
                    resetFilters();
                    setLastId("");
                  }}
                  disabled={isDefaultFilter}
                  className={classNames(
                    "text-md font-medium uppercase text-teal-400 hover:text-teal-300",
                    {
                      "cursor-not-allowed !text-gray-400": isDefaultFilter,
                    }
                  )}
                >
                  {t("button.clear")}
                </button>
              </div>
            </div>
          ) : (
            <div className="flex h-9 items-center justify-between space-x-4 text-[12px] px-5">
              <div className="flex w-full items-center justify-between space-x-6 rounded-md border border-gray-600 py-3 px-4">
                <div className="w-full border-b border-teal-700">
                  <input
                    className="border-none bg-transparent w-full p-0 text-md text-gray-500 placeholder:text-gray-500 dark:text-gray-400 dark:placeholder:text-gray-400"
                    type="text"
                    id="saveFilter"
                    name="saveFilter"
                    placeholder={t("name_saved_filter")}
                    defaultValue={selectedSingleTag?.label}
                    onChange={(e) => {
                      setSavedFilterName(e.target.value);
                    }}
                  />
                </div>
                <div className="flex space-x-3 items-center">
                  {(editFilter.isLoading || addFilter.isLoading) && (
                    <Loader containerClassName="!py-0" />
                  )}
                  <button
                    disabled={
                      !savedFilterName || editFilter.isLoading || addFilter.isLoading
                    }
                    onClick={onSaveFilter}
                    className={classNames(
                      "text-md font-medium uppercase text-teal-400 dark:text-teal-300",
                      {
                        "!text-gray-400 dark:!text-gray-400":
                          !savedFilterName || editFilter.isLoading || addFilter.isLoading,
                      }
                    )}
                  >
                    {t("button.save")}
                  </button>
                </div>
              </div>
            </div>
          )}

          {savedFilterList?.length > 0 && !enableNewFilterLayout && (
            <div className="space-y-3 px-5">
              <div className="bg-gray-100 py-1 text-[12px] font-semibold dark:bg-transparent">
                <p>{t("saved_filters")}</p>
              </div>
              <div
                className={classNames("flex items-center space-x-3 overflow-x-auto", {
                  "gap-2": enableNewFilterLayout,
                })}
              >
                {/* {savedFilterList?.map((tag, index) => ( */}
                <SingleSelectTag
                  options={savedFilterList}
                  onChange={(label, value, id) => {
                    handleSingleTagchange(label, value, id);
                  }}
                  isSearch={true}
                  handleDelete={handleDeleteSavedFilter}
                  selected={selectedSingleTag}
                  filters={filters}
                  className={classNames({
                    "max-w-[160px]": !enableNewFilterLayout,
                    "max-w-[200px]": enableNewFilterLayout,
                  })}
                  labelClassName={classNames(
                    "text-ellipsis overflow-hidden whitespace-nowrap",
                    {
                      "text-sm font-medium": enableNewFilterLayout,
                    }
                  )}
                  showDeleteOnHover={true}
                />
                {/* ))} */}
              </div>
            </div>
          )}

          {!isFullWidthWorklist &&
            savedFilterList?.length > 0 &&
            enableNewFilterLayout && (
              <div className="flex flex-wrap gap-4 py-2 px-5 overflow-y-visible min-h-fit">
                {savedFilterList.map((item, index) => {
                  // In multi-select modal mode, use selectedFiltersInModal as the source of truth
                  // Otherwise, check all selection sources
                  let isSelected;
                  if (isLC && enableNewFilterLayout) {
                    // In modal multi-select mode, only check selectedFiltersInModal for selection state
                    isSelected = selectedFiltersInModal.some(
                      (filter) => filter.id === item.id
                    );
                  } else {
                    // Single-select mode: check all sources
                    const isSelectedSingle =
                      selectedSingleTag?.id === item.id || filters?.id === item.id;
                    const isSelectedMulti = filters?.selectedFilters?.some(
                      (filter) => filter.id === item.id
                    );
                    isSelected = isSelectedSingle || isSelectedMulti;
                  }
                  const isHovered = hoveredFilterId === item.id;
                  return (
                    <div
                      key={item.id}
                      className={classNames(
                        "flex items-center gap-3 px-3 py-3 rounded-[8px] cursor-pointer transition-colors whitespace-nowrap group border min-h-fit relative",
                        {
                          "border-2 border-teal-400": isSelected,
                          "bg-gray-100 hover:text-teal-500 dark:bg-gray-800 dark:border-gray-700":
                            !isSelected,
                        }
                      )}
                      style={{ lineHeight: "1.5" }}
                      onClick={() => {
                        // In multi-select mode, allow toggling selection
                        if (isLC && enableNewFilterLayout) {
                          handleSingleTagchange(item.label, item.values, item.id);
                        } else if (!isSelected) {
                          // Single-select mode: only apply if not already selected
                          handleSingleTagchange(item.label, item.values, item.id);
                        }
                      }}
                      onMouseEnter={() => setHoveredFilterId(item.id)}
                      onMouseLeave={() => setHoveredFilterId(null)}
                    >
                      <span
                        className={classNames("font-medium", {
                          "text-teal-500 dark:text-white": isSelected,
                          "text-teal-600 dark:text-teal-400 dark:group-hover:text-white":
                            !isSelected,
                        })}
                        style={{ lineHeight: "1.5" }}
                      >
                        {item.label}
                      </span>
                      {isSelected && (
                        <div
                          onClick={(e) => {
                            e.stopPropagation();
                            trackEvent(MIXPANEL_EVENTS.FILTER_UPDATED, {
                              action: "deselect",
                            });
                            // In multi-select mode, toggle selection (deselect)
                            if (isLC && enableNewFilterLayout) {
                              handleSingleTagchange(item.label, item.values, item.id);
                            } else {
                              // Single-select mode: clear the selected filter
                              setSelectedSingleTag(null);
                              setSavedFilterName("");
                              setFiltersData(cloneDeep(INITIAL_FILTERS_CONFIG));
                            }
                          }}
                          className="h-5 w-5 flex justify-center items-center shadow-md z-10 bg-teal-400 rounded-full cursor-pointer"
                        >
                          <AwesomeIcon
                            name={faXmark}
                            size="xs"
                            className="text-gray-700"
                          />
                        </div>
                      )}
                      {isHovered && (
                        <div
                          onClick={(e) => {
                            e.stopPropagation();
                            trackEvent(MIXPANEL_EVENTS.FILTER_DELETE_OPENED, {
                              label: item.label,
                            });
                            setFilterToDelete({ id: item.id, label: item.label });
                            setIsDeleteModalOpen(true);
                          }}
                          className="absolute -top-4 -right-3 h-6 w-6 flex justify-center items-center shadow-md z-10 bg-red-500 border border-red-500 rounded-full"
                        >
                          <AwesomeIcon name={faTrash} size="sm" className="text-white" />
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}

          {enableNewFilterLayout ? (
            <>
              {/* TASK GROUP */}
              <div className="space-y-4 px-5">
                <div className="flex items-center gap-2 pb-2">
                  <div className="font-semibold uppercase text-gray-600 dark:text-gray-400 whitespace-nowrap">
                    {t("label.task") || "TASK"}
                  </div>
                  <div className="flex-1 border-b border-gray-300 dark:border-gray-700"></div>
                </div>

                {/* TASK DUE - Date Range (separate from Patient date range) */}
                <div className="space-y-3">
                  <div className="text-[12px] font-semibold uppercase">
                    {t("label.task_due") || "TASK DUE"}
                  </div>
                  <DateRangeInput
                    tooltip={t("task_due_date") || t("scan_upload_date")}
                    placeholder={t("date_filter_placeholder")}
                    value={filtersData.taskDueDateRange ?? [null, null]}
                    highlightToday
                    enableMonthRange
                    onChange={(value) => {
                      trackEvent(MIXPANEL_EVENTS.FILTER_DATE_RANGE_SET, {
                        type: "task_due",
                      });
                      setFiltersData({
                        ...filtersData,
                        taskDueDateRange: value,
                      });
                    }}
                  />
                  <div className="flex flex-wrap gap-2">
                    <Tag
                      label={t("filter.today")}
                      onClick={() => {
                        trackEvent(MIXPANEL_EVENTS.FILTER_QUICK_DATE_SELECTED, {
                          type: "task_due",
                          label: "today",
                        });
                        const today = new Date();
                        const startDate = startOfDay(today);
                        const endDate = endOfDay(today);
                        setFiltersData({
                          ...filtersData,
                          taskDueDateRange: [startDate, endDate],
                        });
                      }}
                    />
                    <Tag
                      label={t("filter.yesterday")}
                      onClick={() => {
                        trackEvent(MIXPANEL_EVENTS.FILTER_QUICK_DATE_SELECTED, {
                          type: "task_due",
                          label: "yesterday",
                        });
                        const yesterday = new Date();
                        yesterday.setDate(yesterday.getDate() - 1);
                        const startDate = startOfDay(yesterday);
                        const endDate = endOfDay(yesterday);
                        setFiltersData({
                          ...filtersData,
                          taskDueDateRange: [startDate, endDate],
                        });
                      }}
                    />
                    <Tag
                      label={t("label.next_14_days") || "Next 14 days"}
                      onClick={() => {
                        trackEvent(MIXPANEL_EVENTS.FILTER_QUICK_DATE_SELECTED, {
                          type: "task_due",
                          label: "next_14_days",
                        });
                        const today = new Date();
                        const endDateObj = new Date();
                        endDateObj.setDate(endDateObj.getDate() + 14);
                        const startDate = startOfDay(today);
                        const endDate = endOfDay(endDateObj);
                        setFiltersData({
                          ...filtersData,
                          taskDueDateRange: [startDate, endDate],
                        });
                      }}
                    />
                  </div>
                </div>

                {/* TASK STATUS */}
                <div className="space-y-3">
                  <div className="text-[12px] font-semibold uppercase">
                    {t("label.task_status") || "TASK STATUS"}
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <Tag
                      label={t("key.task_status.overdue") || "Overdue"}
                      onClick={() => {
                        trackEvent(MIXPANEL_EVENTS.FILTER_STATUS_SELECTED, {
                          value: "overdue",
                        });
                        const currentStatuses = get(filtersData, ["task_status"], []);
                        setFiltersData({
                          ...filtersData,
                          task_status: xor(["overdue"], currentStatuses),
                        });
                      }}
                      isSelected={get(filtersData, ["task_status"], []).includes(
                        "overdue"
                      )}
                    />
                    <Tag
                      label={t("key.task_status.upcoming") || "Upcoming"}
                      onClick={() => {
                        trackEvent(MIXPANEL_EVENTS.FILTER_STATUS_SELECTED, {
                          value: "upcoming",
                        });
                        const currentStatuses = get(filtersData, ["task_status"], []);
                        setFiltersData({
                          ...filtersData,
                          task_status: xor(["upcoming"], currentStatuses),
                        });
                      }}
                      isSelected={get(filtersData, ["task_status"], []).includes(
                        "upcoming"
                      )}
                    />
                    <Tag
                      label={t("key.task_status.urgent") || "Urgent"}
                      onClick={() => {
                        trackEvent(MIXPANEL_EVENTS.FILTER_STATUS_SELECTED, {
                          value: "urgent",
                        });
                        const currentStatuses = get(filtersData, ["task_status"], []);
                        setFiltersData({
                          ...filtersData,
                          task_status: xor(["urgent"], currentStatuses),
                        });
                      }}
                      isSelected={get(filtersData, ["task_status"], []).includes(
                        "urgent"
                      )}
                    />
                    <Tag
                      label={t("key.task_status.done") || "Done"}
                      onClick={() => {
                        trackEvent(MIXPANEL_EVENTS.FILTER_STATUS_SELECTED, {
                          value: "done",
                        });
                        const currentStatuses = get(filtersData, ["task_status"], []);
                        setFiltersData({
                          ...filtersData,
                          task_status: xor(["done"], currentStatuses),
                        });
                      }}
                      isSelected={get(filtersData, ["task_status"], []).includes("done")}
                    />
                  </div>
                </div>

                {/* TASK OWNER */}
                <div className="space-y-3">
                  <div className="text-[12px] font-semibold uppercase">
                    {t("label.task_owner") || "TASK OWNER"}
                  </div>
                  <DropdownMenu
                    placeholder={t("label.select_task_owners", "Select task owners")}
                    options={taskOwnerOptions}
                    onChange={(values) => handleDropdownChange("task_owner", values)}
                    selectedOptions={filtersData.task_owner || []}
                    labelMappings={taskOwnerLabelMappings}
                    workspaceData={[]}
                    category="task_owner"
                    skipPermissionCheck
                    showUserRolePill={false}
                  />
                </div>
                {/* TASK TYPE */}
                <div className="space-y-3">
                  <div className="text-[12px] font-semibold uppercase">
                    {t("label.task_type", "Task Type")}
                  </div>
                  <DropdownMenu
                    placeholder={t("label.select_task_types", "Select task types")}
                    options={taskLabelOptions}
                    onChange={(values) => handleDropdownChange("task_labels", values)}
                    selectedOptions={filtersData.task_labels || []}
                    labelMappings={taskLabelMappings}
                    workspaceData={[]}
                    category="task_labels"
                    skipPermissionCheck
                    showUserRolePill={false}
                  />
                </div>
              </div>
            </>
          ) : (
            <>
              {/* Original Layout */}
              <div className="space-y-3 px-5">
                <div className="text-[12px] font-semibold uppercase">{t("date_key")}</div>
                <DateRangeInput
                  tooltip={t("scan_upload_date")}
                  placeholder={t("date_filter_placeholder")}
                  value={filtersData.dateRange}
                  onChange={handleDateRangeChange}
                />
                <div className="flex flex-wrap gap-3">
                  <Tag label={t("filter.today")} onClick={() => setDate(0, 0, "today")} />
                  <Tag
                    label={t("filter.yesterday")}
                    onClick={() => setDate(-1, -1, "yesterday")}
                  />
                  <Tag
                    label={t("filter.last_7_days")}
                    onClick={() => setDate(-6, 0, "last_7_days")}
                  />
                </div>
              </div>
            </>
          )}

          {/* PATIENT GROUP - conditionally show header, but filters always rendered */}
          {enableNewFilterLayout && (
            <div className="px-5">
              <div className="flex items-center gap-2 pb-2">
                <div className="font-semibold uppercase text-gray-600 dark:text-gray-400 whitespace-nowrap">
                  {t("patient_key") || "PATIENT"}
                </div>
                <div className="flex-1 border-b border-gray-300 dark:border-gray-700"></div>
              </div>
            </div>
          )}

          {/* LC-specific filters: Program Type, AI Recommendation, Manual Review, Program Status, Review - First in Patient group */}
          {isLC &&
            (programTypeOptions?.length > 0 ||
              aiRecommendationOptions?.length > 0 ||
              manualReviewOptions?.length > 0 ||
              programStatusOptions?.length > 0 ||
              reviewOptions?.length > 0) && (
              <div className="pb-5 space-y-5 !mt-2">
                {/* Program Type */}
                {programTypeOptions?.length > 0 && (
                  <div className="space-y-3 px-5">
                    <div className="text-[12px] font-semibold uppercase">
                      {t("label.program_type") || "PROGRAM TYPE"}
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {programTypeOptions.map((option) => (
                        <Tag
                          key={option.value}
                          label={option.label}
                          onClick={() => {
                            trackEvent(MIXPANEL_EVENTS.FILTER_CUSTOM_FIELD_SET, {
                              category: "program_type",
                              value: option.value,
                            });
                            const getProgramTypeTag = workspaceTags.find(
                              (tag) => tag?.additional?.identifier === "program_type"
                            );
                            const customTags = cloneDeep(
                              get(filtersData, ["custom_tags"], [])
                            );
                            let programTypeEntry = Array.isArray(customTags)
                              ? customTags.find(
                                  (item) =>
                                    (item.tag_id ?? item.id) === getProgramTypeTag?.id
                                )
                              : null;
                            if (programTypeEntry) {
                              programTypeEntry.value = [].concat(
                                programTypeEntry.value ?? []
                              );
                              if (programTypeEntry.value.includes(option.value)) {
                                programTypeEntry.value = programTypeEntry.value.filter(
                                  (value) => value !== option.value
                                );
                                if (programTypeEntry.value?.length === 0) {
                                  remove(
                                    customTags,
                                    (item) =>
                                      (item.tag_id ?? item.id) === getProgramTypeTag?.id
                                  );
                                }
                              } else {
                                programTypeEntry.value.push(option.value);
                              }
                            } else {
                              programTypeEntry = {
                                tag_id: getProgramTypeTag?.id,
                                value: [option.value],
                                logic: "in",
                              };
                              customTags.push(programTypeEntry);
                            }
                            setFiltersData({
                              ...filtersData,
                              custom_tags: customTags,
                            });
                          }}
                          isSelected={[]
                            .concat(
                              get(filtersData, ["custom_tags"], [])?.find(
                                (item) =>
                                  (item.tag_id ?? item.id) ===
                                  specialCustomTagIds?.program_type
                              )?.value ?? []
                            )
                            .includes(option.value)}
                        />
                      ))}
                    </div>
                  </div>
                )}

                {/* AI Recommendation */}
                {aiRecommendationOptions?.length > 0 && (
                  <div className="space-y-3 px-5">
                    <div className="text-[12px] font-semibold uppercase">
                      {t("label.ai_recommendation") || "AI RECOMMENDATION"}
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {aiRecommendationOptions.map((option) => (
                        <Tag
                          key={option.value}
                          label={option.label}
                          onClick={() => {
                            trackEvent(MIXPANEL_EVENTS.FILTER_CUSTOM_FIELD_SET, {
                              category: "ai_recommendation",
                              value: option.value,
                            });
                            const getAIRecommendationTag = workspaceTags.find(
                              (tag) => tag?.additional?.identifier === "ai_recommendation"
                            );
                            const customTags = cloneDeep(
                              get(filtersData, ["custom_tags"], [])
                            );
                            let aiRecommendationEntry = Array.isArray(customTags)
                              ? customTags.find(
                                  (item) =>
                                    (item.tag_id ?? item.id) ===
                                    getAIRecommendationTag?.id
                                )
                              : null;
                            if (aiRecommendationEntry) {
                              aiRecommendationEntry.value = [].concat(
                                aiRecommendationEntry.value ?? []
                              );
                              if (aiRecommendationEntry.value.includes(option.value)) {
                                aiRecommendationEntry.value =
                                  aiRecommendationEntry.value.filter(
                                    (value) => value !== option.value
                                  );
                                if (aiRecommendationEntry.value.length === 0) {
                                  remove(
                                    customTags,
                                    (item) =>
                                      (item.tag_id ?? item.id) ===
                                      getAIRecommendationTag?.id
                                  );
                                }
                              } else {
                                aiRecommendationEntry.value.push(option.value);
                              }
                            } else {
                              aiRecommendationEntry = {
                                tag_id: getAIRecommendationTag?.id,
                                value: [option.value],
                                logic: "in",
                              };
                              customTags.push(aiRecommendationEntry);
                            }
                            setFiltersData({
                              ...filtersData,
                              custom_tags: customTags,
                            });
                          }}
                          isSelected={[]
                            .concat(
                              get(filtersData, ["custom_tags"], [])?.find(
                                (item) =>
                                  (item.tag_id ?? item.id) ===
                                  specialCustomTagIds?.ai_recommendation
                              )?.value ?? []
                            )
                            .includes(option.value)}
                        />
                      ))}
                    </div>
                  </div>
                )}

                {/* Manual Review */}
                {manualReviewOptions?.length > 0 && (
                  <div className="space-y-3 px-5">
                    <div className="text-[12px] font-semibold uppercase">
                      {t("label.manual_review") || "MANUAL REVIEW"}
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {manualReviewOptions.map((option) => (
                        <Tag
                          key={option.value}
                          label={option.label}
                          onClick={() => {
                            trackEvent(MIXPANEL_EVENTS.FILTER_CUSTOM_FIELD_SET, {
                              category: "manual_review",
                              value: option.value,
                            });
                            const getManualReviewTag = workspaceTags.find(
                              (tag) => tag?.additional?.identifier === "manual_review"
                            );
                            const customTags = cloneDeep(
                              get(filtersData, ["custom_tags"], [])
                            );
                            let manualReviewEntry = Array.isArray(customTags)
                              ? customTags.find(
                                  (item) =>
                                    (item.tag_id ?? item.id) === getManualReviewTag?.id
                                )
                              : null;
                            if (manualReviewEntry) {
                              manualReviewEntry.value = [].concat(
                                manualReviewEntry.value ?? []
                              );
                              if (manualReviewEntry.value.includes(option.value)) {
                                manualReviewEntry.value = manualReviewEntry.value.filter(
                                  (value) => value !== option.value
                                );
                                if (manualReviewEntry.value.length === 0) {
                                  remove(
                                    customTags,
                                    (item) =>
                                      (item.tag_id ?? item.id) === getManualReviewTag?.id
                                  );
                                }
                              } else {
                                manualReviewEntry.value.push(option.value);
                              }
                            } else {
                              manualReviewEntry = {
                                tag_id: getManualReviewTag?.id,
                                value: [option.value],
                                logic: "in",
                              };
                              customTags.push(manualReviewEntry);
                            }
                            setFiltersData({
                              ...filtersData,
                              custom_tags: customTags,
                            });
                          }}
                          isSelected={[]
                            .concat(
                              get(filtersData, ["custom_tags"], [])?.find(
                                (item) =>
                                  (item.tag_id ?? item.id) ===
                                  specialCustomTagIds?.manual_review
                              )?.value ?? []
                            )
                            .includes(option.value)}
                        />
                      ))}
                    </div>
                  </div>
                )}

                {/* Program Status */}
                {programStatusOptions?.length > 0 && (
                  <div className="space-y-3 px-5">
                    <div className="text-[12px] font-semibold uppercase">
                      {t("label.program_status") || "PROGRAM STATUS"}
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {programStatusOptions.map((option) => (
                        <Tag
                          key={option.value}
                          label={option.label}
                          onClick={() => {
                            trackEvent(MIXPANEL_EVENTS.FILTER_CUSTOM_FIELD_SET, {
                              category: "program_status",
                              value: option.value,
                            });
                            const getProgramStatusTag = workspaceTags.find(
                              (tag) => tag?.additional?.identifier === "program_status"
                            );
                            const customTags = cloneDeep(
                              get(filtersData, ["custom_tags"], [])
                            );
                            let programStatusEntry = Array.isArray(customTags)
                              ? customTags.find(
                                  (item) =>
                                    (item.tag_id ?? item.id) === getProgramStatusTag?.id
                                )
                              : null;
                            if (programStatusEntry) {
                              programStatusEntry.value = [].concat(
                                programStatusEntry.value ?? []
                              );
                              if (programStatusEntry.value.includes(option.value)) {
                                programStatusEntry.value =
                                  programStatusEntry.value.filter(
                                    (value) => value !== option.value
                                  );
                                if (programStatusEntry.value?.length === 0) {
                                  remove(
                                    customTags,
                                    (item) =>
                                      (item.tag_id ?? item.id) === getProgramStatusTag?.id
                                  );
                                }
                              } else {
                                programStatusEntry.value.push(option.value);
                              }
                            } else {
                              programStatusEntry = {
                                tag_id: getProgramStatusTag?.id,
                                value: [option.value],
                                logic: "in",
                              };
                              customTags.push(programStatusEntry);
                            }
                            setFiltersData({
                              ...filtersData,
                              custom_tags: customTags,
                            });
                          }}
                          isSelected={[]
                            .concat(
                              get(filtersData, ["custom_tags"], [])?.find(
                                (item) =>
                                  (item.tag_id ?? item.id) ===
                                  specialCustomTagIds?.program_status
                              )?.value ?? []
                            )
                            .includes(option.value)}
                        />
                      ))}
                    </div>
                  </div>
                )}

                {/* Review */}
                {reviewOptions?.length > 0 && (
                  <div className="space-y-3 px-5">
                    <div className="text-[12px] font-semibold uppercase">
                      {t("label.review") || "REVIEW"}
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {reviewOptions.map((option) => (
                        <Tag
                          key={option.value}
                          label={option.label}
                          onClick={() => {
                            trackEvent(MIXPANEL_EVENTS.FILTER_CUSTOM_FIELD_SET, {
                              category: "review",
                              value: option.value,
                            });
                            const getReviewTag = workspaceTags.find(
                              (tag) => tag?.additional?.identifier === "review"
                            );
                            const customTags = cloneDeep(
                              get(filtersData, ["custom_tags"], [])
                            );
                            let reviewEntry = Array.isArray(customTags)
                              ? customTags.find(
                                  (item) => (item.tag_id ?? item.id) === getReviewTag?.id
                                )
                              : null;
                            if (reviewEntry) {
                              reviewEntry.value = [].concat(reviewEntry.value ?? []);
                              if (reviewEntry.value.includes(option.value)) {
                                reviewEntry.value = reviewEntry.value.filter(
                                  (value) => value !== option.value
                                );
                                if (reviewEntry.value.length === 0) {
                                  remove(
                                    customTags,
                                    (item) =>
                                      (item.tag_id ?? item.id) === getReviewTag?.id
                                  );
                                }
                              } else {
                                reviewEntry.value.push(option.value);
                              }
                            } else {
                              reviewEntry = {
                                tag_id: getReviewTag?.id,
                                value: [option.value],
                                logic: "in",
                              };
                              customTags.push(reviewEntry);
                            }
                            setFiltersData({
                              ...filtersData,
                              custom_tags: customTags,
                            });
                          }}
                          isSelected={[]
                            .concat(
                              get(filtersData, ["custom_tags"], [])?.find(
                                (item) =>
                                  (item.tag_id ?? item.id) === specialCustomTagIds?.review
                              )?.value ?? []
                            )
                            .includes(option.value)}
                        />
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

          {/* Original Patient Filters - always rendered, unchanged */}
          {enableNewFilterLayout && (
            <div className="!space-y-3 px-5 !mt-2" key="patient-date-range">
              {/* Original Date Range - part of Patient group when new layout */}
              <div className="text-[12px] font-semibold uppercase">
                {t("date_created_key")}
              </div>
              <DateRangeInput
                tooltip={t("scan_upload_date")}
                placeholder={t("date_filter_placeholder")}
                value={filtersData.dateRange}
                onChange={handleDateRangeChange}
                highlightToday
                enableMonthRange
              />
              <div className="flex flex-wrap gap-3">
                <Tag label={t("filter.today")} onClick={() => setDate(0, 0, "today")} />
                <Tag
                  label={t("filter.last_7_days")}
                  onClick={() => setDate(-6, 0, "last_7_days")}
                />
                <Tag label={t("filter.last_month")} onClick={() => setLastMonth()} />
              </div>
            </div>
          )}

          <div className={classNames("space-y-3 px-5")}>
            <div className="bg-gray-100 py-1 text-[12px] font-semibold dark:bg-transparent">
              <p className="uppercase">{t("sites_key")}</p>
            </div>
            <DropdownMenu
              placeholder={t("sites_placeholder")}
              options={Object.keys(sitesOptions)}
              workspaceData={sources}
              onChange={(values) => handleDropdownChange("site", values)}
              selectedOptions={get(filtersData, ["site", "tags", "dropdownMenuTags"], [])}
              labelMappings={{ ...labelMappings, ...sitesOptions }}
              tagsList={tagsList}
              category={"sites"}
              showAdminTagOnly
              skipPermissionCheck
            />
          </div>
          {Object.keys(filtersList).map((filterKey) => {
            const categoryObj = filtersList[filterKey];
            const category = Object.keys(categoryObj)[0];
            if (
              isLC &&
              ![
                "dateRange",
                "site",
                "present_findings",
                "absent_findings",
                "modalities",
                "patient_type",
              ].includes(category)
            )
              return null;
            const dicom_filter_length = size(
              get(filtersData, ["dicom_upload", "tags", "tags"], [])
            );
            const currentDropdownMenuTags = get(
              filtersData,
              [category, "tags", "dropdownMenuTags"],
              {}
            );

            const present_findings = [
              ...present_findinds_selected_tags,
              ...present_findings_selected_dropdown_tags,
            ];

            const absent_findings = [
              ...absent_findinds_selected_tags,
              ...absent_findings_selected_dropdown_tags,
            ];

            const present_findings_length = size(present_findings);
            const absent_findings_length = size(absent_findings);

            if (category === "site") return "";

            if (category === "dicom_upload" && isLC) return "";

            if (category === "report_edited_by") {
              return (
                <div
                  className={classNames("space-y-3 px-5", { hidden: isOnpremise })}
                  key={category}
                >
                  <div className="bg-gray-100 py-1 text-[12px] font-semibold dark:bg-transparent">
                    <p>{t(`mapping.${category}`).toUpperCase()}</p>
                  </div>
                  <SelectUser
                    onChange={(values) =>
                      handleDropdownChange("report_edited_by", values)
                    }
                    value={get(
                      filtersData,
                      ["report_edited_by", "tags", "dropdownMenuTags"],
                      []
                    )}
                  />
                </div>
              );
            }

            const selectedJoin = get(filtersData, [category, "join"], "");
            const selectedJoinOption = findingsJoinOptions.find(
              (f) => f.value === selectedJoin
            );
            return (
              <div
                className={classNames("space-y-3 px-5", {
                  hidden: isOnpremise && category === "report_status",
                })}
                key={category}
              >
                <div className="bg-gray-100 py-1 text-md dark:bg-transparent">
                  <div className="font-semibold">
                    {t(`mapping.${category}`)?.toUpperCase()}
                  </div>
                  {(category === "present_findings" ||
                    category === "absent_findings") && (
                    <FlyoutMenu
                      panelClassName={classNames({ "dark:!bg-gray-800": isMobile })}
                      cta={() => (
                        <div className="flex text-sm text-gray-400 items-center justify-start">
                          <div className="text-left">
                            {get(selectedJoinOption, ["title"], "")}
                          </div>
                          <AwesomeIcon name={faPenToSquare} size="lg" className="ml-3" />
                        </div>
                      )}
                    >
                      <RadioList
                        value={selectedJoin}
                        name="join"
                        options={findingsJoinOptions}
                        className="!space-y-6"
                        onChange={(val) => handleFindingsJoinChange(category, val)}
                      />
                    </FlyoutMenu>
                  )}
                </div>

                {!isEmpty(get(categoryObj, [category, "tags", "dropdownTags"], null)) && (
                  <div className="flex w-full flex-wrap gap-3">
                    {get(categoryObj, [category, "tags", "dropdownTags"], []).map(
                      (tagObj) => {
                        const tagLabel = Object.keys(tagObj)[0];
                        const tagOptions = tagObj[tagLabel];
                        let selectedOptions;
                        try {
                          selectedOptions = filtersData[
                            category
                          ].tags.dropdownTags.filter(
                            (obj) => Object.keys(obj)[0] === tagLabel
                          )[0][tagLabel];
                        } catch (error) {
                          selectedOptions = [];
                        }

                        return (
                          <DropdownTag
                            key={tagLabel}
                            label={tagLabel}
                            options={tagOptions}
                            onChange={(values) =>
                              handleDropdownTagChange(category, tagLabel, values)
                            }
                            selectedOptions={selectedOptions}
                            labelMappings={labelMappings}
                          />
                        );
                      }
                    )}
                  </div>
                )}
                <div className="flex w-full flex-wrap gap-3">
                  {/* Normal Tags */}
                  {!isEmpty(get(categoryObj, [category, "tags", "tags"], null)) &&
                    get(categoryObj, [category, "tags", "tags"], []).map((tag) => (
                      <Tag
                        disabled={
                          (category === "dicom_upload" &&
                            (present_findings_length || absent_findings_length)) ||
                          ((category === "present_findings" ||
                            category === "absent_findings") &&
                            dicom_filter_length > 0) ||
                          (category === "present_findings" &&
                            absent_findings.includes(tag)) ||
                          (category === "absent_findings" &&
                            present_findings.includes(tag))
                        }
                        key={tag}
                        label={tag}
                        onClick={() => handleTagClick(category, tag)}
                        isSelected={filtersData[category]?.tags?.tags?.indexOf(tag) > -1}
                        mapping={true}
                      />
                    ))}
                  {(category === "present_findings" || category === "absent_findings") &&
                    !isEmpty(
                      get(categoryObj, [category, "tags", "dropdownMenuTags"], null)
                    ) && (
                      <>
                        {Object.keys(currentDropdownMenuTags).map((section) =>
                          currentDropdownMenuTags[section].map((tag) => (
                            <Tag
                              key={tag}
                              label={tag}
                              onClick={(e) => {
                                e.stopPropagation();
                                const _currentDropdownMenuTags = cloneDeep(
                                  currentDropdownMenuTags
                                );
                                const updatedValues = xor(
                                  [tag],
                                  _currentDropdownMenuTags[section]
                                );
                                _currentDropdownMenuTags[section] = updatedValues;
                                handleDropdownChange(category, _currentDropdownMenuTags);
                              }}
                              isSelected
                              mapping={true}
                            />
                          ))
                        )}
                        <FindingsSelector
                          title={t(`mapping.${category}`)?.toUpperCase()}
                          description={get(selectedJoinOption, ["title"], "")}
                          disabled={dicom_filter_length > 0}
                          findings={get(
                            categoryObj,
                            [category, "tags", "dropdownMenuTags"],
                            null
                          )}
                          value={get(
                            filtersData,
                            [category, "tags", "dropdownMenuTags"],
                            {}
                          )}
                          category={category}
                          onChange={(values) => handleDropdownChange(category, values)}
                          disabledValues={
                            category === "present_findings"
                              ? absent_findings
                              : present_findings
                          }
                        />
                      </>
                    )}
                </div>
              </div>
            );
          })}
          {isLC && (
            <CustomFieldsFilters
              filtersData={filtersData}
              setFiltersData={(data) => {
                trackEvent(MIXPANEL_EVENTS.FILTER_CUSTOM_FIELD_SET);
                setFiltersData(data);
              }}
              selectedWorkspaceId={selectedWorkspace?.value}
              showFilters={showFilters}
            />
          )}
        </div>

        {/* Footer */}
        <div
          className={classNames(
            "flex py-2 items-center space-x-4 border-t border-gray-400 px-4 text-md",
            {
              "!pb-4": isWebView(),
              "sticky bottom-0 w-full dark:bg-gray-900": isMobile,
            }
          )}
        >
          <div className="flex items-center w-full space-x-4 py-3 pb-3">
            <button
              onClick={() => {
                trackEvent(MIXPANEL_EVENTS.FILTER_PANEL_CLOSED);
                setShowFilters(false);
                setIsSaveInput(false);
              }}
              className="rounded-6 bg-gray-600 py-4 px-5 w-1/2 text-base text-gray-100 hover:bg-gray-700"
            >
              {t("button.cancel")}
            </button>
            <button
              disabled={isDefaultFilter}
              className={classNames(
                "rounded-6 bg-teal-500 py-4 px-5 text-base w-1/2 text-gray-100 hover:bg-teal-600",
                {
                  "cursor-not-allowed !bg-teal-900 !text-gray-200": isDefaultFilter,
                }
              )}
              onClick={() => {
                // In multi-select mode, apply selected filters from selectedFiltersInModal
                if (isLC && enableNewFilterLayout && selectedFiltersInModal.length > 0) {
                  applyFilters(filtersData, currentSearch, false);
                } else {
                  applyFilters(filtersData);
                }
              }}
            >
              {t("button.apply")}
            </button>
          </div>
        </div>
      </div>
      <Modal
        show={isDeleteModalOpen}
        onClose={handleCancelDelete}
        title="Deleting saved filters"
        className="!w-auto min-w-[400px]"
        closeOnClickAway={true}
        zIndex="z-[1000]"
      >
        <div className="p-6">
          <p className="text-gray-900 dark:text-gray-100">
            This saved filter will be permanently deleted.
          </p>
        </div>
        <div className="flex items-center justify-end p-4 border-t border-gray-200 dark:border-gray-700">
          <div className="flex items-center gap-4">
            <Button className="w-auto" onClick={handleCancelDelete}>
              Cancel
            </Button>
            <Button isDanger className="w-auto" onClick={handleConfirmDelete}>
              Delete filter
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default SearchFilters;
